import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { emitCoachEvent } from '@/coach/coachApi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  ArrowLeft, Save, Layers, BarChart3, TrendingUp, Rocket,
  Plus, X, Sparkles, Loader2, Filter, Info, Eye, FileText,
  PanelLeft, PanelRight, PanelTop, LayoutGrid as LayoutGridIcon, SlidersHorizontal, Table, Calendar, Palette,
  ImageIcon, Type, Square, Circle, Download, Printer, Bell, Server, ExternalLink, Code,
  ChevronLeft, ChevronRight, RotateCcw, Users, ChevronDown, Copy, FilePlus2, Zap, History, AlertTriangle, RefreshCw
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
// html2canvas loaded dynamically when needed (see thumbnail capture)
import { toast } from 'sonner';
import { ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useReportUndoRedo } from '@/hooks/useReportUndoRedo';
import ShelfBuilder from '@/components/report-builder/ShelfBuilder';
import ReportVersionsDialog from '@/components/report-builder/ReportVersionsDialog';
import { VisualErrorBoundary } from '@/components/report-builder/VisualErrorBoundary';
import MiddleTabContent from '@/components/report-builder/MiddleTabContent';
import LeftFieldPanel from '@/components/report-builder/LeftFieldPanel';
import BuilderDialogs from '@/components/report-builder/BuilderDialogs';
import BuilderHeader from '@/components/report-builder/BuilderHeader';
import { genId } from '@/components/report-builder/genId';
import { BinFieldDialog } from '@/components/report-builder/BinFieldDialog';
import { SlicerControl } from '@/components/report-builder/SlicerControl';
import { ChartRenderer } from '@/components/report-builder/ChartRenderer';
import { TableRenderer } from '@/components/report-builder/TableRenderer';
import { PivotTableRenderer } from '@/components/report-builder/PivotTableRenderer';
import { LossTriangleRenderer } from '@/components/report-builder/LossTriangleRenderer';
import { ComparisonTableRenderer } from '@/components/report-builder/ComparisonTableRenderer';
import { ForecastTile } from '@/components/explore/ForecastTile';
import { SentinelSummaryTile } from '@/components/explore/SentinelSummaryTile';
import { AnalyticsPanel } from '@/components/report-builder/AnalyticsPanel';
import { isObjectVisual } from '@/components/report-builder/FreeVisual';
import { ReportCanvas, useSnapPosition, useAlignmentGuides } from '@/components/report-builder/ReportCanvas';
import { safeEvalMath, createFormatValue, withMeasureUnits } from '@/components/report-builder/formatUtils';
import { PillContextMenu } from '@/components/report-builder/PillContextMenu';
import { ModelPicker } from '@/components/report-builder/ModelPicker';
import { getTransformedData as computeTransformedData } from '@/components/report-builder/dataTransformations';
import { handleExportData } from '@/components/report-builder/exportHandler';
import { AdvancedTab } from '@/components/report-builder/AdvancedTab';
import FieldStatsModal from '@/components/FieldStatsModal';
import { PageLayoutTab } from '@/components/report-builder/PageLayoutTab';
import { captureCanvasThumbnail, persistReport, cloneCurrentReport } from '@/lib/reportPersistence';
import { makeZoneDropHandlers } from '@/components/report-builder/zoneDropHandlers';
import { getTemplateById, buildStateFromTemplate } from '@/lib/insightTemplates';
import { runVisualQuery, filtersForSelection } from '@/lib/visualQueryEngine';
import { TileMeasureControls } from '@/components/report-builder/TileMeasureControls';
import { useAuth } from '@/contexts/AuthContext';
import { useCoEdit } from '@/lib/useCoEdit';
import { CoEditBar, CoEditCursors } from '@/components/dashboards/CoEditPresence';
import ShareViewDialog from '@/components/ShareViewDialog';
import { getRegisteredVizType } from '@/lib/customVizRegistry';
import { useConfirmDialog } from '@/components/ConfirmDialog';
import { ReportZoneArea } from '@/components/report-builder/ReportZoneArea';
import { PageZoneArea } from '@/components/report-builder/PageZoneArea';
import { GlobalFilterBar } from '@/components/report-builder/GlobalFilterBar';
import { getKpiIcon } from '@/components/report-builder/kpiIcons';
import { PageNavChrome, normalizeNavStyle } from '@/components/dashboards/PageNavChrome';
import { FreeObjectVisual } from '@/components/report-builder/FreeObjectVisual';
import { makeRenderPageContent, makeRenderPageHeader, makeRenderPageFooter } from '@/components/report-builder/reportCanvasRenderers';
import { useUndoRedoKeyboard } from '@/components/report-builder/useUndoRedoKeyboard';
import { useGlobalShortcut } from '@/hooks/useGlobalShortcut';
import { useVisualContextMenuActions } from '@/components/report-builder/useVisualContextMenuActions';
import { QueryViewDialog } from '@/components/report-builder/QueryViewDialog';
import { PromotionDialog } from '@/components/PromotionDialog';
import { VisualContextMenuWrapper } from '@/components/report-builder/VisualContextMenuWrapper';
import { DualAxisPicker } from '@/components/report-builder/DualAxisPicker';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { getTerm } from '@/lib/branding';
import { resolveLabel, entityQualifiedLabel } from '@/lib/fieldLabel';
import { isDateField } from '@/lib/dateSlicerUtils';
import { SlicerSettingsDialog } from '@/components/report-builder/SlicerSettingsDialog';
import { getSlicerSetting, sortSlicerValues, resolveDefaultSelection } from '@/lib/slicerSettings';

const API = process.env.REACT_APP_BACKEND_URL;

// Compare two react-grid-layout arrays by position only (i/x/y/w/h). Used to
// stop RGL's onLayoutChange → setState → re-render → onLayoutChange feedback
// loop that otherwise fires on every render (e.g. when renaming a visual).
function _gridLayoutsEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const byI = {};
  for (const it of a) byI[it.i] = it;
  for (const it of b) {
    const p = byI[it.i];
    if (!p || p.x !== it.x || p.y !== it.y || p.w !== it.w || p.h !== it.h) return false;
  }
  return true;
}

export default function ReportBuilderPage({ tabId, initialReportId, reloadNonce, onLabelChange, onReportIdChange, onClose, onOpenReport, isTabbed }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const ws = useWorkspace();
  const canCertify = ['admin', 'super_admin', 'tenant_admin', 'steward', 'data_steward', 'owner'].includes(user?.role);
  const [searchParams] = useSearchParams();
  const editReportId = initialReportId !== undefined ? initialReportId : searchParams.get('id');
  const modeParam = searchParams.get('mode');
  const templateParam = searchParams.get('template');
  const isImported = searchParams.get('imported') === 'true';
  // Reload-on-reopen support: track unsaved state + the last-applied reload nonce
  // so re-opening a tab refreshes it without clobbering unsaved edits.
  const savedCleanRef = useRef(true);
  const loadedNonceRef = useRef('INIT');
  // Mirrors the loaded report's server `_version` so the reopen effect can tell
  // a genuine external save (version bumped) from a mere navigate-back focus.
  const reportVersionRef = useRef(null);
  // Per-tab draft persistence (workbench only): an unsaved in-progress insight
  // is snapshotted to sessionStorage keyed by tabId so navigating away and back
  // restores it instead of resetting to the model picker.
  const draftKey = tabId ? `rb_draft_${tabId}` : null;
  const draftRestoredRef = React.useRef(false);
  // Autosave/crash-recovery for SAVED reports: silently snapshot in-progress
  // edits to sessionStorage (survives an accidental refresh) and restore them
  // after the server load, so layout changes are never lost. Cleared on save.
  const recoveryKey = editReportId ? `rb_recovery_${editReportId}` : null;
  const recoveryHandledRef = React.useRef(false);
  // True while a Save/Save-As is in flight so the debounced recovery autosave
  // can't resurrect a pre-save snapshot after the recovery key is cleared.
  const savingRef = React.useRef(false);
  const [serverLoadDone, setServerLoadDone] = useState(false);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [allFields, setAllFields] = useState([]);
  const [modelDateFieldOptions, setModelDateFieldOptions] = useState([]);
  const [modelDefaultDateField, setModelDefaultDateField] = useState(null);
  const [fiscalStartMonth, setFiscalStartMonth] = useState(1);
  const { confirmFn, ConfirmDialogElement } = useConfirmDialog();
  const [reportName, setReportName] = useState('');
  const [reportVersion, setReportVersion] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [reportDesc, setReportDesc] = useState('');
  const [reportId, setReportId] = useState(null);
  const [modelId, setModelId] = useState('');
  const [importSessionId, setImportSessionId] = useState('');
  const [models, setModels] = useState([]);
  const [modelChosen, setModelChosen] = useState(!!editReportId);
  const [showImportReviewNote, setShowImportReviewNote] = useState(false);
  const [reviewedVisualIds, setReviewedVisualIds] = useState(() => new Set());
  const [resolvedUnmappedCols, setResolvedUnmappedCols] = useState(() => new Set());
  const [fieldSearch, setFieldSearch] = useState('');
  const [leftPanelTab, setLeftPanelTab] = useState('fields');
  const [libraryVisuals, setLibraryVisuals] = useState([]);

  // Resizable builder panels (Fields | Config | Canvas)
  const [fieldsPanelWidth, setFieldsPanelWidth] = useState(() => Number(localStorage.getItem('rb_fields_w')) || 224);
  const [configPanelWidth, setConfigPanelWidth] = useState(() => Number(localStorage.getItem('rb_config_w')) || 288);
  const [fieldsCollapsed, setFieldsCollapsed] = useState(() => localStorage.getItem('rb_fields_collapsed') === '1');
  const [configCollapsed, setConfigCollapsed] = useState(() => localStorage.getItem('rb_config_collapsed') === '1');
  useEffect(() => { localStorage.setItem('rb_fields_w', String(fieldsPanelWidth)); }, [fieldsPanelWidth]);
  useEffect(() => { localStorage.setItem('rb_config_w', String(configPanelWidth)); }, [configPanelWidth]);
  useEffect(() => { localStorage.setItem('rb_fields_collapsed', fieldsCollapsed ? '1' : '0'); }, [fieldsCollapsed]);
  useEffect(() => { localStorage.setItem('rb_config_collapsed', configCollapsed ? '1' : '0'); }, [configCollapsed]);
  const resetPanelSizes = () => {
    setFieldsPanelWidth(224); setConfigPanelWidth(288);
    setFieldsCollapsed(false); setConfigCollapsed(false);
    setLayoutMenuOpen(false);
  };
  const startPanelResize = (e, which) => {
    e.preventDefault();
    const startX = e.clientX;
    const startFields = fieldsPanelWidth;
    const startConfig = configPanelWidth;
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      if (which === 'fields') setFieldsPanelWidth(Math.max(180, Math.min(480, startFields + dx)));
      else setConfigPanelWidth(Math.max(220, Math.min(560, startConfig + dx)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = ''; document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
  };

  // Undo/Redo history — removed duplicate, using existing system below

  // Auto-refresh timer
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(0);
  const autoRefreshRef = React.useRef(null);

  // Bookmarks
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarkMenuOpen, setBookmarkMenuOpen] = useState(false);
  
  // Multi-page support
  const [pages, setPages] = useState([{ id: 'page_1', name: 'Page 1' }]);
  const [activePageId, setActivePageId] = useState('page_1');
  const [pageNavStyle, setPageNavStyle] = useState('tabs'); // 'tabs' | 'dropdown'
  
  // Visuals are per-page — stored as { pageId: [visuals] }
  const [pageVisuals, setPageVisuals] = useState({ page_1: [{ id: 'v1', vizType: 'bar_chart', title: 'Visual 1', rowDims: [], colDim: '', valueMeasures: [], filterDims: [] }] });
  const visuals = useMemo(() => pageVisuals[activePageId] || [], [pageVisuals, activePageId]);
  const setVisuals = (fn) => setPageVisuals(prev => ({ ...prev, [activePageId]: typeof fn === 'function' ? fn(prev[activePageId] || []) : fn }));
  const visualsRef = React.useRef(visuals);
  React.useEffect(() => { visualsRef.current = visuals; }, [visuals]);
  const [activeVisual, setActiveVisual] = useState('v1');
  const [visualResults, setVisualResults] = useState({});
  const [loadingVisuals, setLoadingVisuals] = useState(new Set());
  // Slow-query UX: after 5s of waiting, show a floating spinner + Cancel pill.
  const [slowQuery, setSlowQuery] = useState(false);
  const slowTimerRef = useRef(null);
  useEffect(() => {
    if (loadingVisuals.size > 0) {
      if (!slowTimerRef.current) slowTimerRef.current = setTimeout(() => setSlowQuery(true), 5000);
    } else {
      if (slowTimerRef.current) { clearTimeout(slowTimerRef.current); slowTimerRef.current = null; }
      setSlowQuery(false);
    }
    return () => {};
  }, [loadingVisuals]);
  const cancelRunningQuery = () => {
    setLoadingVisuals(new Set());
    setSlowQuery(false);
    if (slowTimerRef.current) { clearTimeout(slowTimerRef.current); slowTimerRef.current = null; }
  };
  // Session-only "Pause Auto-Run" switch: when paused, field/shape edits mark a
  // visual stale instead of re-querying. Always starts Live (never persisted).
  const [autoRunPaused, setAutoRunPaused] = useState(false);
  const [staleVisualIds, setStaleVisualIds] = useState(new Set());
  const [loadingAiVisuals, setLoadingAiVisuals] = useState(new Set());
  const [aiContents, setAiContents] = useState({}); // { vizId: 'text content' } // { vizId: { result, pivotResult } }
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slicerValues, setSlicerValues] = useState({});
  const [slicerSelections, setSlicerSelections] = useState({});
  const [drillPath, setDrillPath] = useState([]);
  const [drillFilters, setDrillFilters] = useState([]);
  const [globalFilterDims, setGlobalFilterDims] = useState([]);
  const [globalFilterValues, setGlobalFilterValues] = useState({});
  // Phase 2 — page-scope filters: keyed by page id → dims/values that apply
  // only to that page's visuals (dashboard-scope = globalFilter* above).
  const [pageFilterDims, setPageFilterDims] = useState({});
  const [pageFilterValues, setPageFilterValues] = useState({});
  const [globalSlicerValues, setGlobalSlicerValues] = useState({});
  const [crossFilter, setCrossFilter] = useState(null);
  const [globalFilterPosition, setGlobalFilterPosition] = useState('top'); // 'top' | 'left' | 'right' | 'bottom' // { field, value, sourceVisualId }
  const [cascadeSlicers, setCascadeSlicers] = useState(true); // per-insight: slicers cross-filter each other
  const [showGrid, setShowGrid] = useState(true);
  const [showLegend, setShowLegend] = useState(true);
  const [refLineValue, setRefLineValue] = useState('');
  const [refLineLabel, setRefLineLabel] = useState('');
  const [condRules, setCondRules] = useState([]);
  const [statsOpen, setStatsOpen] = useState(false);
  const [queryExplanation, setQueryExplanation] = useState(null);
  const [visualQueryInfo, setVisualQueryInfo] = useState({}); // { vizId: { pipeline, summary } }
  const [queryViewOpen, setQueryViewOpen] = useState(false);
  // Per-visual "Other" series drill-in: when a viz id is true, the Top-N
  // "Other" bucket is expanded to show its individual rolled-up series.
  const [otherExpanded, setOtherExpanded] = useState({});
  const toggleOtherExpanded = (vizId) => setOtherExpanded(p => ({ ...p, [vizId]: !p[vizId] }));
  const [queryViewVisualId, setQueryViewVisualId] = useState(null);
  const [visualConfigs, setVisualConfigs] = useState({});
  const getVizConfig = (vizId) => visualConfigs[vizId] || {};
  const setVizConfig = (vizId, updates) => setVisualConfigs(prev => ({ ...prev, [vizId]: { ...(prev[vizId] || {}), ...updates } }));
  const [textBlocks, setTextBlocks] = useState([]);
  const [layoutPreset, setLayoutPreset] = useState(() => localStorage.getItem('rb_layout') || 'default');
  const [layoutMenuOpen, setLayoutMenuOpen] = useState(false);
  const [markColor, setMarkColor] = useState('');
  const [markSize, setMarkSize] = useState('');
  const [markLabel, setMarkLabel] = useState('');
  const [markDetail, setMarkDetail] = useState('');
  const [parameters, setParameters] = useState([]);
  const [paramValues, setParamValues] = useState({});
  const [reportTheme, setReportTheme] = useState(null); // { theme_id, colors }
  const [availableThemes, setAvailableThemes] = useState([]);
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [tableCalcs, setTableCalcs] = useState({});
  const [dataSets, setDataSets] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [highlightField, setHighlightField] = useState(null);
  const [highlightValue, setHighlightValue] = useState(null);
  const [pageField, setPageField] = useState('');
  const [pageValues, setPageValues] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageAnimating, setPageAnimating] = useState(false);

  // ─── Designer Mode (Dashboard vs Report) ───
  const [designerMode, setDesignerMode] = useState(modeParam === 'report' ? 'report' : 'dashboard'); // 'dashboard' | 'report'
  const [reportPageSize, setReportPageSize] = useState('letter');
  const [reportOrientation, setReportOrientation] = useState('portrait');
  const [reportMargins, setReportMargins] = useState({ top: 48, right: 48, bottom: 48, left: 48 });
  const [reportHeader, setReportHeader] = useState({ enabled: true, height: 60, repeatOnPages: true, showOnFirstPage: true, logoUrl: '', title: '{{report_name}}', titleFontSize: 16, titleBold: true });
  const [reportFooter, setReportFooter] = useState({ enabled: true, height: 40, text: 'Page {{page}} of {{total_pages}}  |  {{date}}', fontSize: 9 });
  const [reportTableSpanPages, setReportTableSpanPages] = useState(true);
  const [reportTableRepeatHeader, setReportTableRepeatHeader] = useState(true);
  const [mobileLayout, setMobileLayout] = useState({});  // { vizId: { hidden: false, order: 0, fullWidth: true } }
  const [reportZoom, setReportZoom] = useState(75);
  const [reportSnapSize, setReportSnapSize] = useState(8);
  const [reportShowGrid, setReportShowGrid] = useState(true);
  const [reportShowRulers, setReportShowRulers] = useState(true);
  const [alignmentGuides, setAlignmentGuides] = useState({ x: null, y: null });
  const snap = useSnapPosition(reportSnapSize);
  const findAlignmentGuides = useAlignmentGuides(visuals, activeVisual, 5);
  
  // ─── Multi-Page Report State ───
  const [reportPages, setReportPages] = useState([{ id: 'rp_1', name: 'Page 1', hasManualBreak: false }]);
  const [activeReportPageIndex, setActiveReportPageIndex] = useState(0);
  
  // Get visuals for a specific report page (by _reportPageIndex)
  const getVisualsForReportPage = useCallback((pageIdx) => {
    return visuals.filter(v => (v._reportPageIndex ?? 0) === pageIdx);
  }, [visuals]);
  
  // Add a new report page
  const addReportPage = useCallback(() => {
    const newId = genId('rp_');
    const newPage = { id: newId, name: `Page ${reportPages.length + 1}`, hasManualBreak: false };
    setReportPages(prev => [...prev, newPage]);
    setActiveReportPageIndex(reportPages.length);
    setSavedClean(false);
  }, [reportPages.length]);
  
  // Delete a report page
  const deleteReportPage = useCallback((pageIdx) => {
    if (reportPages.length <= 1) return;
    // Remove page and reassign visuals to previous page
    setVisuals(prev => prev.map(v => {
      if ((v._reportPageIndex ?? 0) === pageIdx) {
        return { ...v, _reportPageIndex: Math.max(0, pageIdx - 1) };
      }
      if ((v._reportPageIndex ?? 0) > pageIdx) {
        return { ...v, _reportPageIndex: (v._reportPageIndex ?? 0) - 1 };
      }
      return v;
    }));
    setReportPages(prev => prev.filter((_, i) => i !== pageIdx));
    setActiveReportPageIndex(prev => Math.max(0, Math.min(prev, reportPages.length - 2)));
    setSavedClean(false);
  }, [reportPages.length]);
  
  // Insert manual page break at current position
  const insertManualPageBreak = useCallback(() => {
    const newId = genId('rp_');
    const newPage = { id: newId, name: `Page ${reportPages.length + 1}`, hasManualBreak: true };
    setReportPages(prev => {
      const newPages = [...prev];
      newPages.splice(activeReportPageIndex + 1, 0, newPage);
      return newPages.map((p, i) => ({ ...p, name: `Page ${i + 1}` }));
    });
    setActiveReportPageIndex(prev => prev + 1);
    setSavedClean(false);
  }, [reportPages, activeReportPageIndex]);

  const PAGE_SIZES_MAP = { letter: { w: 816, h: 1056 }, a4: { w: 794, h: 1123 }, legal: { w: 816, h: 1344 } };

  // Pages animation via useEffect — cleans up interval properly
  useEffect(() => {
    if (!pageAnimating || !pageValues.length) return;
    const iv = setInterval(() => {
      setPageIndex(prev => {
        const next = prev + 1;
        if (next >= pageValues.length) {
          setPageAnimating(false);
          return 0;
        }
        return next;
      });
    }, 1200);
    return () => clearInterval(iv);
  }, [pageAnimating, pageValues.length]);
  const [dualAxisField, setDualAxisField] = useState('');
  const setLayout = (preset) => { setLayoutPreset(preset); localStorage.setItem('rb_layout', preset); setLayoutMenuOpen(false); };

  const LAYOUTS = {
    default: { name: 'Classic (Fields | Config | Canvas)', dir: 'row', panels: ['fields', 'config', 'canvas'] },
    fields_right: { name: 'Canvas Left (Canvas | Config | Fields)', dir: 'row', panels: ['canvas', 'config', 'fields'] },
    config_bottom: { name: 'Wide Canvas (Fields | Canvas over Config)', dir: 'row', panels: ['fields', 'canvas_over_config'] },
    stacked: { name: 'Stacked (Config+Fields top, Canvas bottom)', dir: 'col', panels: ['top_panels', 'canvas'] },
    minimal: { name: 'Minimal (Config | Canvas)', dir: 'row', panels: ['config', 'canvas'] },
  };
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseField, setBrowseField] = useState(null);
  const [browseData, setBrowseData] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('excel');
  const [exportSettings, setExportSettings] = useState({});
  const [exporting, setExporting] = useState(false);
  const [fieldOverrides, setFieldOverrides] = useState({});
  const [propsOpen, setPropsOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [middleTab, setMiddleTab] = useState('data');
  const [savedClean, setSavedClean] = useState(false); // 'data' | 'visuals' | 'advanced'
  const [draftSavedAt, setDraftSavedAt] = useState(0); // last silent autosave time (for the "Draft saved" tick)
  const [gridLayouts, setGridLayouts] = useState(null); // persisted layout
  const canvasRef = React.useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(1200);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    // Ignore transient zero-width measurements (inactive tabs use display:none,
    // and a browser resize can momentarily report 0) — otherwise canvasWidth
    // goes negative and the visuals collapse to an unresizable sliver.
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setCanvasWidth(Math.max(w - 48, 320));
      }
    });
    ro.observe(el);
    const w0 = el.clientWidth;
    if (w0 > 0) setCanvasWidth(Math.max(w0 - 48, 320));
    return () => ro.disconnect();
  }, [modelChosen]);
  const [propsField, setPropsField] = useState(null);
  const [propsForm, setPropsForm] = useState({});
  const [pillCtxMenu, setPillCtxMenu] = useState(null);
  const [visualCtxMenu, setVisualCtxMenu] = useState(null);
  const [corrPopupData, setCorrPopupData] = useState(null);
  const [alertFromVizOpen, setAlertFromVizOpen] = useState(false);
  const [alertFromVizData, setAlertFromVizData] = useState({ metric: '', operator: 'gt', threshold: '', name: '', frequency: 'daily', recipients: '' }); // { vizId, data }
  const [slicerDisplayModes, setSlicerDisplayModes] = useState({});
  const [slicerSettings, setSlicerSettings] = useState({});
  const [settingsDialogDim, setSettingsDialogDim] = useState(null);
  const [secondYAxisConfig, setSecondYAxisConfig] = useState({}); // { vizId: { field, chartType } }
  const [dateParts, setDateParts] = useState({}); // { fieldName: 'year'|'quarter'|'month'|'week'|'day'|null } // { dimName: 'dropdown'|'multi'|'buttons'|'radio'|'search' }

  // ─── Per-visual data accessors ───
  const cv = useMemo(() => visuals.find(v => v.id === activeVisual) || visuals[0] || {}, [visuals, activeVisual]);
  const importUnmappedColumns = useMemo(() => {
    const seen = new Set(); const out = [];
    (visuals || []).forEach(v => (v && v._imported ? (v._unmappedColumns || []) : []).forEach(c => {
      const k = String(c).toLowerCase();
      if (c && !seen.has(k)) { seen.add(k); out.push(c); }
    }));
    return out;
  }, [visuals]);
  const rowDims = useMemo(() => cv.rowDims || [], [cv]);
  const colDim = cv.colDim || '';
  const valueMeasures = useMemo(() => cv.valueMeasures || [], [cv]);
  const filterDims = useMemo(() => cv.filterDims || [], [cv]);
  const result = (visualResults[activeVisual] || {}).result || null;
  const pivotResult = (visualResults[activeVisual] || {}).pivotResult || null;

  const setRowDims = (fn) => { setSavedClean(false); setVisuals(prev => prev.map(v => v.id === activeVisual ? { ...v, rowDims: typeof fn === 'function' ? fn(v.rowDims || []) : fn } : v)); };
  const setColDim = (val) => { setSavedClean(false); setVisuals(prev => prev.map(v => v.id === activeVisual ? { ...v, colDim: val } : v)); };
  const setValueMeasures = (fn) => { setSavedClean(false); setVisuals(prev => prev.map(v => v.id === activeVisual ? { ...v, valueMeasures: typeof fn === 'function' ? fn(v.valueMeasures || []) : fn } : v)); };
  const setFilterDims = (fn) => { setSavedClean(false); setVisuals(prev => prev.map(v => v.id === activeVisual ? { ...v, filterDims: typeof fn === 'function' ? fn(v.filterDims || []) : fn } : v)); };
  // Word-cloud color-gradient measure: stored on the per-visual config, and
  // mirrored into valueMeasures so its values are fetched by the query.
  const colorMeasure = getVizConfig(activeVisual).colorMeasure || '';
  const setColorMeasure = (name) => {
    setSavedClean(false);
    const prevCM = getVizConfig(activeVisual).colorMeasure;
    setVizConfig(activeVisual, { colorMeasure: name || '' });
    if (name) {
      setValueMeasures(p => p.includes(name) ? p : [...p, name]);
    } else if (prevCM) {
      setValueMeasures(p => p.filter(x => x !== prevCM));
    }
  };
  // Pin a dimension field to a specific entity so the query resolves it against
  // the entity the user actually picked (e.g. Claim vs Policy book_end_date),
  // instead of the backend's name-based heuristic. Additive: no pin = old behavior.
  const pinFieldEntity = (fieldName, entityId) => {
    if (!fieldName || !entityId) return;
    setSavedClean(false);
    setVisuals(prev => prev.map(v => v.id === activeVisual ? { ...v, dimEntities: { ...(v.dimEntities || {}), [fieldName]: entityId } } : v));
  };
  const setResult = (val) => setVisualResults(prev => ({ ...prev, [activeVisual]: { ...(prev[activeVisual] || {}), result: val } }));
  const setPivotResult = (val) => setVisualResults(prev => ({ ...prev, [activeVisual]: { ...(prev[activeVisual] || {}), pivotResult: val } }));

  const isDirty = !savedClean && (modelChosen || reportName.trim().length > 0);
  const { confirmNavigation, UnsavedDialog } = useUnsavedChanges(isDirty, 'You have unsaved report changes. Discard and leave?');

  const handlePillRightClick = (x, y, fieldName, shelfLabel) => {
    setPillCtxMenu({ x, y, field: fieldName, shelf: shelfLabel });
  };

  const removeFromAllShelves = (fieldName) => {
    saveUndoState(`Removed ${fieldName}`);
    setRowDims(p => p.filter(x => x !== fieldName));
    if (colDim === fieldName) setColDim('');
    setValueMeasures(p => p.filter(x => x !== fieldName));
    setFilterDims(p => p.filter(x => x !== fieldName));
    setSlicerSelections(p => { const c = { ...p }; delete c[fieldName]; return c; });
  };

  // Save per-slicer author settings and immediately preview the resolved
  // default selection in the builder (so authors see what viewers will get).
  const handleSaveSlicerSetting = async (dim, setting) => {
    setSlicerSettings(prev => ({ ...prev, [dim]: setting }));
    const isGlobalDim = globalFilterDims.includes(dim) || Object.keys(pageFilterDims || {}).some(pid => (pageFilterDims[pid] || []).includes(dim));
    const vals = (globalSlicerValues[dim] || slicerValues[dim] || []);
    const isDate = isDateField(allFields.find(f => f.name === dim));
    const ds = setting.defaultSelection || { type: 'all' };
    let topValue = null;
    if (ds.type === 'top_measure' && ds.measure && modelId) {
      try {
        const r = await api.executeQuery({ model_id: modelId, dimensions: [dim], measures: [ds.measure], filters: [], limit: 500 });
        const rows = (r.data || []).filter(x => x[dim] != null);
        rows.sort((a, b) => (Number(b[ds.measure]) || 0) - (Number(a[ds.measure]) || 0));
        topValue = rows.length ? rows[0][dim] : null;
      } catch { /* ignore */ }
    }
    const resolved = resolveDefaultSelection({ setting, values: vals, isDate, reportId: editReportId, dim, topValue });
    if (isGlobalDim) {
      setGlobalFilterValues(p => { const n = { ...p }; if (resolved) n[dim] = resolved; else delete n[dim]; return n; });
    } else {
      // Apply to every per-visual slicer instance for this dimension.
      setSlicerSelections(p => {
        const n = { ...p };
        (visuals || []).forEach(v => { if ((v.filterDims || []).includes(dim)) n[v.id + '_' + dim] = resolved; });
        return n;
      });
    }
  };


  // "Clear dot on review": once the user edits a visual's shelves, its
  // auto-mapped "guess" dots stop nagging.
  const markActiveReviewed = useCallback(() => {
    setReviewedVisualIds(prev => (prev.has(activeVisual) ? prev : new Set(prev).add(activeVisual)));
  }, [activeVisual]);

  // "One-click add unmapped": click a column the importer couldn't match and
  // we try to resolve it against the loaded model fields and drop it on the
  // right shelf (Values for measures, Rows for dimensions). If nothing matches,
  // prefill the field search so the user can find and drag it.
  const addUnmappedColumn = useCallback((col) => {
    const normStr = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const n = normStr(col);
    const toks = String(col || '').toLowerCase().split(/[^a-z0-9]+/).filter(t => t.length >= 3);
    let match = null;
    for (const f of allFields) {
      if (normStr(f.name) === n || normStr(f.display_name) === n) { match = f; break; }
    }
    if (!match) for (const f of allFields) {
      const fn = normStr(f.name), fd = normStr(f.display_name);
      if ((fn && fn.length >= 4 && (fn.includes(n) || n.includes(fn))) ||
          (fd && fd.length >= 4 && (fd.includes(n) || n.includes(fd)))) { match = f; break; }
    }
    if (!match && toks.length) for (const f of allFields) {
      const ftoks = String(f.display_name || f.name).toLowerCase().split(/[^a-z0-9]+/);
      if (toks.every(t => ftoks.includes(t))) { match = f; break; }
    }
    if (match) {
      if (match.semantic_type === 'measure') setValueMeasures(p => p.includes(match.name) ? p : [...p, match.name]);
      else setRowDims(p => p.includes(match.name) ? p : [...p, match.name]);
      setResolvedUnmappedCols(s => new Set(s).add(String(col).toLowerCase()));
      markActiveReviewed();
      toast.success(`Added "${match.display_name || match.name}" to ${match.semantic_type === 'measure' ? 'Values' : 'Rows'}`);
    } else {
      setFieldSearch(col);
      setLeftPanelTab('fields');
      toast.message(`No matching field for "${col}" — search the fields list and drag it onto a shelf`);
    }
  }, [allFields, markActiveReviewed]);

  const handleBrowseField = async (field) => {
    setBrowseField(field);
    setBrowseOpen(true);
    setBrowseLoading(true);
    setBrowseData([]);
    try {
      const res = await api.executeQuery({ model_id: modelId, dimensions: [field.name], measures: [], limit: 500 });
      const vals = [...new Set((res.data || []).map(r => r[field.name]).filter(v => v !== null && v !== undefined))].sort((a, b) => { if (typeof a === 'number' && typeof b === 'number') return a - b; return String(a).localeCompare(String(b)); }).slice(0, 50);
      setBrowseData(vals);
    } catch (err) { toast.error(err.message); }
    finally { setBrowseLoading(false); }
  };

  // Right-click "View Data" on a field pill — opens the new FieldStatsModal.
  // Distinct from `handleBrowseField` above which renders sample values inline
  // in the legacy Browse Data dialog; this one shows distinct counts +
  // numeric summaries via the dedicated /field-stats endpoint.
  const [viewDataField, setViewDataField] = useState(null);
  const handleViewField = (field) => setViewDataField(field?.name || null);
  const [binField, setBinField] = useState(null);
  const handleBinField = (field) => setBinField(field);
  const reloadFieldsAfterBin = () => { loadFieldsFromModel(); };

  const handleExport = async () => {
    await handleExportData({
      result, exportFormat, exportSettings, reportName, reportDesc, modelId,
      rowDims, colDim, valueMeasures, allFields, fieldOverrides, visuals,
      setExporting, setExportOpen
    });
  };

  const openFieldProperties = (fieldName) => {
    const f = allFields.find(ff => ff.name === fieldName);
    const ovr = fieldOverrides[fieldName] || {};
    setPropsField(fieldName);
    setPropsForm({
      displayName: ovr.displayName || resolveLabel(allFields, fieldName),
      format: ovr.format || (f ? f.format || '' : ''),
      sortOrder: ovr.sortOrder || 'default',
      aggregation: ovr.aggregation || (f ? f.aggregation || '' : ''),
      numberDecimals: ovr.numberDecimals || '2',
      showTotals: ovr.showTotals !== false,
      width: ovr.width || 'auto',
    });
    setPropsOpen(true);
  };

  const saveFieldProperties = () => {
    setFieldOverrides(prev => ({ ...prev, [propsField]: { ...propsForm } }));
    setSavedClean(false);
    setPropsOpen(false);
    toast.success('Field properties updated');
  };

  // Undo/redo history — see hooks/useReportUndoRedo
  const { undoStack, redoStack, pushUndo, saveUndoState, undo, redo, revertTo } = useReportUndoRedo({
    snapshot: () => ({ pageVisuals, visualConfigs }),
    restore: (state) => {
      if (state.pageVisuals) setPageVisuals(state.pageVisuals);
      if (state.visualConfigs) setVisualConfigs(state.visualConfigs);
    },
    onChange: () => setSavedClean(false),
  });

  const formatValue = React.useMemo(() => createFormatValue(allFields, fieldOverrides, fiscalStartMonth), [allFields, fieldOverrides, fiscalStartMonth]);
  // Fields whose format the user explicitly set in Properties — these win over
  // the server's auto-detected `_measure_units` (so a chosen $ / K / M sticks).
  const overriddenFmtFields = React.useMemo(() => new Set(Object.keys(fieldOverrides || {}).filter(k => (fieldOverrides[k] || {}).format)), [fieldOverrides]);
  // Date-typed dimension fields offered for the relative date-range preset.
  // When the modeler configured a default date + allowed alternates, restrict
  // the picker to those (default first); otherwise fall back to all date fields.
  const allDateFields = React.useMemo(() => (allFields || [])
    .filter(f => f.type === 'dimension' && ['date', 'datetime', 'timestamp', 'time'].includes(String(f.data_type || '').toLowerCase()))
    .map(f => f.name), [allFields]);
  const dateFields = React.useMemo(() => {
    const governed = (modelDateFieldOptions || []).filter(n => allDateFields.includes(n));
    return governed.length ? governed : allDateFields;
  }, [modelDateFieldOptions, allDateFields]);
  // Per-visual formatter: layers the query result's `_measure_units` (e.g. a
  // certified ratio → percent) over the base formatter so certified measures
  // render as percentages on the tile.
  const fmtForVisual = (vid) => withMeasureUnits(formatValue, visualResults[vid]?.result?._measure_units, overriddenFmtFields);

  useEffect(() => { api.getSemanticModels().then(setModels).catch(console.error); api.getThemes().then(setAvailableThemes).catch(() => {}); }, []);

  // Coach: report that the report/insight builder opened (trustworthy UI event)
  // so a walkthrough or autonomous plan step keyed to `builder_opened` advances.
  useEffect(() => { emitCoachEvent('builder_opened', 'reports-new-insight-build'); }, []);


  // Load imported report config from sessionStorage
  useEffect(() => {
    if (isImported && !editReportId) {
      const importedConfig = sessionStorage.getItem('importedReportConfig');
      if (importedConfig) {
        try {
          const config = JSON.parse(importedConfig);
          // Apply imported config
          if (config.designerMode) setDesignerMode(config.designerMode);
          if (config.reportPageSize) setReportPageSize(config.reportPageSize);
          if (config.reportOrientation) setReportOrientation(config.reportOrientation);
          if (config.reportMargins) setReportMargins(config.reportMargins);
          if (config.reportHeader) setReportHeader(config.reportHeader);
          if (config.reportFooter) setReportFooter(config.reportFooter);
          if (config.reportPages) { setReportPages(config.reportPages); setActiveReportPageIndex(0); }
          if (config.visuals && config.visuals.length > 0) {
            setVisuals(config.visuals);
            visualsRef.current = config.visuals;
            setActiveVisual(config.visuals[0]?.id || '');
            // Respect a prior dismissal (survives StrictMode/tab remounts and
            // page refresh) so the banner doesn't nag again for this import.
            const sid = sessionStorage.getItem('importedSessionId') || '';
            const dismissed = sid && localStorage.getItem('inzara.import_review_dismissed.' + sid) === '1';
            setShowImportReviewNote(!dismissed);
          }
          // Set report name if detected
          if (config.reportHeader?.title) {
            const title = config.reportHeader.title.replace(/\{\{.*?\}\}/g, '').trim();
            if (title) setReportName(title);
          }
          // Apply the selected semantic model from import
          const importedModelId = sessionStorage.getItem('importedModelId');
          if (importedModelId) {
            setModelId(importedModelId);
          }
          const importedSessionId = sessionStorage.getItem('importedSessionId');
          if (importedSessionId) setImportSessionId(importedSessionId);
          // Skip the model-selection gate — the import already chose a model
          // and produced visuals, so render the editor with the imported layout.
          setModelChosen(true);
          setSavedClean(false);
          // Defer clearing the handoff: React StrictMode / tab remounts mount
          // this page more than once, and the surviving instance must still be
          // able to read the imported config. Clear after it has settled.
          setTimeout(() => {
            sessionStorage.removeItem('importedReportConfig');
            sessionStorage.removeItem('importedModelId');
            sessionStorage.removeItem('importedSessionId');
          }, 4000);
          toast.success('Report imported successfully!');
        } catch (err) {
          console.error('Failed to load imported config:', err);
        }
      }
    }
  }, [isImported, editReportId]);

  // Hydrate builder from a starter template (?template=<id>) — one-shot per new-insight session
  // so users get a pre-populated layout instead of an empty canvas.
  const templateHydratedRef = React.useRef(false);
  useEffect(() => {
    if (editReportId) return; // never overwrite an existing saved insight
    if (templateHydratedRef.current) return;
    if (!templateParam) return;
    let template = getTemplateById(templateParam);
    if (!template) {
      // Fallback: template stashed by the +New menu
      try {
        const raw = sessionStorage.getItem('inzara.insightTemplate');
        if (raw) template = JSON.parse(raw);
      } catch (_) { /* ignore */ }
    }
    if (!template) return;
    const built = buildStateFromTemplate(template);
    if (!built) return;
    templateHydratedRef.current = true;
    setReportName(built.reportName || '');
    if (onLabelChange) onLabelChange(built.reportName || 'Untitled');
    // Place all template visuals on the first page and mark them active
    setPageVisuals({ page_1: built.visuals });
    visualsRef.current = built.visuals;
    if (built.visuals[0]) setActiveVisual(built.visuals[0].id);
    setGridLayouts(built.gridLayouts);
    setSavedClean(false);
    // Clear the sessionStorage handoff so a subsequent visit without ?template= won't rehydrate.
    try { sessionStorage.removeItem('inzara.insightTemplate'); } catch (_) { /* ignore */ }
    // Strip ?template= from the URL so opening a second tab doesn't re-hydrate.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('template');
      window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
    } catch (_) { /* ignore */ }
    toast.success(`Template "${template.name}" applied — pick a model to populate data`);
  }, [templateParam, editReportId]);

  useEffect(() => {
    // A reopen (openTab bumped reloadNonce for an already-open tab) should pull
    // the latest saved report — but never silently discard unsaved edits, and
    // never fire on a mere navigate-back (the Workbench dock re-navigates to
    // ?id=, which bumps the nonce without anything having changed).
    const prevNonce = loadedNonceRef.current;
    const firstRun = prevNonce === 'INIT';
    loadedNonceRef.current = reloadNonce;
    const isReload = !firstRun && reloadNonce !== prevNonce;
    if (!editReportId) return;

    const doLoad = () => {
      const hasExploreConfig = !!sessionStorage.getItem('exploreInsightConfig');
      setServerLoadDone(false);
      recoveryHandledRef.current = false;
      api.getReport(editReportId).then(report => {
        setReportId(report.report_id);
        // Load bookmarks
        api.getBookmarks(report.report_id).then(bms => setBookmarks(bms || [])).catch(() => {});
        setReportName(report.name || '');
        setReportVersion(report._version ?? null);
        if (onLabelChange) onLabelChange(report.name || 'Untitled');
        setReportDesc(report.description || '');
        const qdef = report.query_definition || {};
        // Check for explore insight config in sessionStorage (backup for save-as-insight)
        const exploreConfigRaw = sessionStorage.getItem('exploreInsightConfig');
        if (exploreConfigRaw) {
          sessionStorage.removeItem('exploreInsightConfig');
          try {
            const ec = JSON.parse(exploreConfigRaw);
            if (ec.query_definition?.model_id) setModelId(ec.query_definition.model_id);
            if (ec.config?.designerMode) setDesignerMode(ec.config.designerMode);
            if (ec.config?.reportPageSize) setReportPageSize(ec.config.reportPageSize);
            if (ec.config?.reportOrientation) setReportOrientation(ec.config.reportOrientation);
            if (ec.config?.showGrid !== undefined) setShowGrid(ec.config.showGrid);
            if (ec.config?.showLegend !== undefined) setShowLegend(ec.config.showLegend);
            
            // Handle multi-page report visuals
            if (ec.config?.pages?.length > 0 && ec.config?.visuals?.length > 0) {
              const ecPages = ec.config.pages;
              setPages(ecPages);
              // Distribute visuals across pages
              const newPageVisuals = {};
              ecPages.forEach((pg, idx) => {
                newPageVisuals[pg.id] = ec.config.visuals.filter(v => (v._reportPageIndex ?? 0) === idx);
              });
              // Fallback: put all on first page if no page index set
              if (Object.values(newPageVisuals).every(arr => arr.length === 0)) {
                newPageVisuals[ecPages[0].id] = ec.config.visuals;
              }
              setPageVisuals(newPageVisuals);
              if (ec.config.visuals[0]) setActiveVisual(ec.config.visuals[0].id);
              visualsRef.current = newPageVisuals[ecPages[0].id] || [];
            } else if (ec.config?.visuals?.length > 0) {
              setVisuals(ec.config.visuals);
              visualsRef.current = ec.config.visuals;
              setActiveVisual(ec.config.visuals[0]?.id || 'v1');
            }
            
            if (ec.config?.visualConfigs) setVisualConfigs(ec.config.visualConfigs);
            if (ec.config?.fieldOverrides) setFieldOverrides(ec.config.fieldOverrides);
            if (ec.config?.gridLayouts) setGridLayouts(ec.config.gridLayouts);
            if (ec.config?.reportHeader) setReportHeader(ec.config.reportHeader);
            if (ec.config?.reportFooter) setReportFooter(ec.config.reportFooter);
            
            setModelChosen(true);
            setSavedClean(true);
            setServerLoadDone(true);
            return;
          } catch (e) { console.error('Failed to parse explore config:', e); }
        }
        if (qdef.model_id) setModelId(qdef.model_id);
        else if (models.length > 0) setModelId(models[0].model_id);
        if (report.config) {
          const c = report.config;
          if (c.visuals && c.visuals.length > 0) {
            // Migrate: if visuals don't have per-visual shelves, populate from global qdef
            const migratedVisuals = c.visuals.map((v, i) => ({
              ...v,
              rowDims: v.rowDims || (i === 0 ? qdef.dimensions || [] : []),
              colDim: v.colDim !== undefined ? v.colDim : (i === 0 ? qdef.column_dimension || c.colDim || '' : ''),
              valueMeasures: v.valueMeasures || (i === 0 ? qdef.measures || [] : []),
              filterDims: v.filterDims || (i === 0 ? c.slicerDimensions || [] : []),
            }));
            setVisuals(migratedVisuals);
            visualsRef.current = migratedVisuals;
            setActiveVisual(migratedVisuals[0]?.id || 'v1');
          }
          if (c.showGrid !== undefined) setShowGrid(c.showGrid);
          if (c.showLegend !== undefined) setShowLegend(c.showLegend);
          if (c.refLineValue) setRefLineValue(c.refLineValue);
          if (c.refLineLabel) setRefLineLabel(c.refLineLabel);
          if (c.condRules) setCondRules(c.condRules);
          if (c.secondYAxisConfig) setSecondYAxisConfig(c.secondYAxisConfig);
          if (c.dateParts) setDateParts(c.dateParts);
          if (c.reportTheme) setReportTheme(c.reportTheme);
          if (c.gridLayouts) setGridLayouts(c.gridLayouts);
          if (c.parameters) setParameters(c.parameters);
          if (c.paramValues) setParamValues(c.paramValues);
          // Restore remembered slicer/filter selections & display modes so a
          // reopened insight keeps its date-slicer grain/periods/presets.
          if (c.slicerSelections) setSlicerSelections(c.slicerSelections);
          if (c.globalFilterValues) setGlobalFilterValues(c.globalFilterValues);
          if (c.globalFilterDims) setGlobalFilterDims(c.globalFilterDims);
          if (c.pageFilterDims) setPageFilterDims(c.pageFilterDims);
          if (c.pageFilterValues) setPageFilterValues(c.pageFilterValues);
          if (c.slicerDisplayModes) setSlicerDisplayModes(c.slicerDisplayModes);
          if (c.slicerSettings) setSlicerSettings(c.slicerSettings);
          if (typeof c.cascadeSlicers === 'boolean') setCascadeSlicers(c.cascadeSlicers);
          if (c.globalFilterPosition) setGlobalFilterPosition(c.globalFilterPosition);
          // Report mode settings
          if (c.designerMode) setDesignerMode(c.designerMode);
          if (c.reportPageSize) setReportPageSize(c.reportPageSize);
          if (c.reportOrientation) setReportOrientation(c.reportOrientation);
          if (c.reportMargins) setReportMargins(c.reportMargins);
          if (c.reportHeader) setReportHeader(c.reportHeader);
          if (c.reportFooter) setReportFooter(c.reportFooter);
          if (c.reportTableSpanPages !== undefined) setReportTableSpanPages(c.reportTableSpanPages);
          if (c.reportTableRepeatHeader !== undefined) setReportTableRepeatHeader(c.reportTableRepeatHeader);
          if (c.visualConfigs) setVisualConfigs(c.visualConfigs);
          if (c.fieldOverrides) setFieldOverrides(c.fieldOverrides);
          if (c.pages) { setPages(c.pages); setActivePageId(c.pages[0]?.id || 'page_1'); }
          if (c.pageVisuals) setPageVisuals(c.pageVisuals);
          else if (c.visuals) {
            // Legacy: migrate visuals to page_1
            setPageVisuals({ page_1: c.visuals });
          }
          if (c.pageNavStyle) setPageNavStyle(c.pageNavStyle);
          if (c.aiContents) setAiContents(c.aiContents);
          if (c.annotations) setAnnotations(c.annotations);
          if (c.mobileLayout) setMobileLayout(c.mobileLayout);
          // Multi-page report support
          if (c.reportPages) { setReportPages(c.reportPages); setActiveReportPageIndex(0); }
        }
        setSavedClean(true);
        setServerLoadDone(true);
      }).catch(err => {
        // Don't show error if explore config was applied from sessionStorage
        if (!hasExploreConfig) toast.error('Failed to load report');
        setServerLoadDone(true);
      });
    };

    if (isReload) {
      // The nonce bumped. Only react if the SAVED report actually changed on
      // the server (its `_version` moved past what we loaded). If it's the same
      // version, this is just a focus/navigate-back — keep the working copy and
      // stay silent. If it changed and we have unsaved edits, warn instead of
      // discarding them; if it changed and we're clean, refresh in place.
      api.getReport(editReportId).then(report => {
        const sv = report._version ?? null;
        const known = reportVersionRef.current;
        const changedExternally = sv !== null && known !== null && sv !== known;
        if (!changedExternally) return;
        if (!savedCleanRef.current) {
          toast('This insight was updated elsewhere — save your changes, then reopen to load the latest.', { icon: 'ℹ️' });
          return;
        }
        doLoad();
      }).catch(() => {});
      return;
    }

    doLoad();
  }, [editReportId, reloadNonce]);

  // Keep an unsaved-edits flag in a ref for the reopen guard above.
  useEffect(() => { savedCleanRef.current = savedClean; }, [savedClean]);
  useEffect(() => { reportVersionRef.current = reportVersion; }, [reportVersion]);

  // Apply a persisted builder-state snapshot back into component state.
  const applyBuilderState = (s) => {
    if (!s) return;
    if (s.modelId) setModelId(s.modelId);
    setModelChosen(true);
    if (s.reportName !== undefined) setReportName(s.reportName || '');
    if (s.reportDesc !== undefined) setReportDesc(s.reportDesc || '');
    if (s.pageVisuals) setPageVisuals(s.pageVisuals);
    if (s.pages) setPages(s.pages);
    if (s.activePageId) setActivePageId(s.activePageId);
    if (s.reportPages) { setReportPages(s.reportPages); setActiveReportPageIndex(0); }
    if (s.visualConfigs) setVisualConfigs(s.visualConfigs);
    if (s.fieldOverrides) setFieldOverrides(s.fieldOverrides);
    if (s.gridLayouts) setGridLayouts(s.gridLayouts);
    if (s.parameters) setParameters(s.parameters);
    if (s.paramValues) setParamValues(s.paramValues);
    if (s.slicerSelections) setSlicerSelections(s.slicerSelections);
    if (s.globalFilterValues) setGlobalFilterValues(s.globalFilterValues);
    if (s.globalFilterDims) setGlobalFilterDims(s.globalFilterDims);
    if (s.pageFilterDims) setPageFilterDims(s.pageFilterDims);
    if (s.pageFilterValues) setPageFilterValues(s.pageFilterValues);
    if (s.slicerDisplayModes) setSlicerDisplayModes(s.slicerDisplayModes);
    if (s.slicerSettings) setSlicerSettings(s.slicerSettings);
    if (typeof s.cascadeSlicers === 'boolean') setCascadeSlicers(s.cascadeSlicers);
    if (s.showGrid !== undefined) setShowGrid(s.showGrid);
    if (s.showLegend !== undefined) setShowLegend(s.showLegend);
    if (s.reportTheme) setReportTheme(s.reportTheme);
    if (s.designerMode) setDesignerMode(s.designerMode);
    if (s.reportPageSize) setReportPageSize(s.reportPageSize);
    if (s.reportOrientation) setReportOrientation(s.reportOrientation);
    if (s.reportMargins) setReportMargins(s.reportMargins);
    if (s.condRules) setCondRules(s.condRules);
    if (s.dateParts) setDateParts(s.dateParts);
    if (s.aiContents) setAiContents(s.aiContents);
    if (s.annotations) setAnnotations(s.annotations);
    if (s.reportHeader) setReportHeader(s.reportHeader);
    if (s.reportFooter) setReportFooter(s.reportFooter);
    const firstV = ((s.pageVisuals && s.pageVisuals[s.activePageId || 'page_1']) || [])[0];
    setActiveVisual(s.activeVisual || firstV?.id || 'v1');
    if (s.reportName && onLabelChange) onLabelChange(s.reportName);
  };

  // Restore an in-progress draft on (re)mount for a NEW (unsaved) insight tab.
  useEffect(() => {
    if (editReportId || !draftKey || draftRestoredRef.current) return;
    let raw = null;
    try { raw = sessionStorage.getItem(draftKey); } catch (_) { raw = null; }
    if (!raw) {
      // The per-tab key survives refresh for draft tabs (the token is in the
      // URL), so the shared singleton fallback is ONLY for legacy non-draft
      // tabs. A fresh draft tab with no snapshot must stay blank — never pull
      // the singleton (that's what resurrected a just-discarded report).
      const isExplicitNew = new URLSearchParams(window.location.search).get('new') === '1';
      const isDraftTab = (tabId || '').startsWith('draft:');
      if (!isExplicitNew && !isDraftTab) {
        try { raw = sessionStorage.getItem('rb_draft_new_singleton'); } catch (_) { raw = null; }
      }
    }
    if (!raw) return;
    let snap = null;
    try { snap = JSON.parse(raw); } catch (_) { return; }
    if (!snap || !snap.modelChosen) return;
    draftRestoredRef.current = true;
    applyBuilderState(snap);
    setSavedClean(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editReportId, draftKey]);

  // Extract the model → field-list loader so we can reuse it for
  // (a) initial mount, and (b) refresh-on-focus so newly-created calc
  // dims / bucket dims / calc measures appear without a page reload.
  const loadFieldsFromModel = useCallback(async () => {
    if (!modelId) return;
    try {
      const m = await api.getSemanticModelFull(modelId);
      setFiscalStartMonth(Number(m.fiscal_year_start_month) || 1);
      // Model date-field governance: default date + allowed alternates (facts first).
      const dnames = [];
      let defField = null;
      const ents = (m.entities || []);
      [...ents].sort((a, b) => (a.entity_type === 'fact' ? -1 : 0) - (b.entity_type === 'fact' ? -1 : 0)).forEach(e => {
        if (e.default_date_field && !dnames.includes(e.default_date_field)) { dnames.push(e.default_date_field); if (!defField) defField = e.default_date_field; }
        (e.allowed_date_fields || []).forEach(f => { if (f && !dnames.includes(f)) dnames.push(f); });
      });
      setModelDateFieldOptions(dnames);
      setModelDefaultDateField(defField);
      const fields = []; const seen = new Set(); const fieldIdxByKey = new Map();
      (m.entities || []).forEach(e => (e.attributes || []).forEach(a => {
        // Dedupe per-entity, NOT globally: the same field name (e.g. book_end_date)
        // can legitimately exist on multiple entities (Policy AND Claim), and each
        // must be shown under its own entity group in the picker.
        const key = (e.entity_id || e.name || '') + '::' + a.name;
        const incoming = { ...a, entity_id: e.entity_id, entity_name: e.name, entity_logical_name: e.logical_name || '' };
        const idx = fieldIdxByKey.get(key);
        if (idx === undefined) { fieldIdxByKey.set(key, fields.length); fields.push(incoming); seen.add(key); }
        else {
          // Same field defined twice on one entity: some imports emit a rich entry
          // PLUS a stub whose display_name is just the shared source-column label
          // (e.g. several amount measures all stubbed as "Claim Amount"). Keep the
          // entry that carries a business_name / description so the real measure
          // name (e.g. "Loss Reserve") wins instead of a duplicate "Claim Amount".
          const cur = fields[idx];
          const score = (f) => (f && f.business_name ? 2 : 0) + (f && f.description ? 1 : 0);
          if (score(incoming) > score(cur)) fields[idx] = { ...cur, ...incoming };
        }
      }));
      (m.calculated_measures || []).forEach(cm => {
        if (!seen.has(cm.name)) {
          const _src = cm.source_entity_id ? ((m.entities || []).find(e => e.entity_id === cm.source_entity_id) || {}) : {};
          fields.push({ name: cm.name, display_name: cm.name, description: cm.name, help: cm.description || '', semantic_type: 'measure', data_type: 'float', aggregation: cm.aggregation || 'sum', formula: cm.formula, is_calculated: true, _isModelMeasure: true, display_folder: cm.display_folder || cm.folder || _src.logical_name || _src.name || '' });
          seen.add(cm.name);
        }
      });
      // Re-register virtual fields (e.g. "Sale Date__1") from currently-loaded visuals.
      const currentVisuals = visualsRef.current || visuals;
      currentVisuals.forEach(v => {
        (v.rowDims || []).forEach(dim => {
          if (dim.includes('__') && !seen.has(dim)) {
            const sourceField = dim.split('__')[0];
            const sourceMeta = fields.find(f => f.name === sourceField);
            if (sourceMeta) {
              fields.push({ ...sourceMeta, name: dim, _sourceField: sourceField });
              seen.add(dim);
            }
          }
        });
      });
      // Qualify duplicate-named DIMENSION fields with an entity-scoped identity
      // (`<entity_id>::<field>`) so two fields sharing a name — e.g.
      // full_legal_name on BOTH Adjuster and Claimant — stop colliding on a
      // shelf, in column labels, and in query result columns. Unique names and
      // measures keep their bare names (backward compatible; the query engine's
      // measure path doesn't parse qualified keys and legacy reports still work).
      const _dimNameCount = {};
      fields.forEach(f => {
        if (f && f.semantic_type !== 'measure' && !f.is_calculated && f.entity_id && !f._sourceField) {
          _dimNameCount[f.name] = (_dimNameCount[f.name] || 0) + 1;
        }
      });
      fields.forEach(f => {
        if (f && f.semantic_type !== 'measure' && !f.is_calculated && f.entity_id && !f._sourceField
            && _dimNameCount[f.name] > 1 && !String(f.name).includes('::')) {
          const base = f.name;
          const baseLabel = f.business_name || f.display_name || f.label || base.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          f._baseName = base;
          f.name = f.entity_id + '::' + base;
          f.business_name = entityQualifiedLabel(f.entity_name, f.entity_logical_name, baseLabel);
          f._baseLabel = baseLabel;
        }
      });
      // Same entity-qualified identity for duplicate-named MEASURE columns that
      // live on multiple facts/entities (e.g. a CLAIM_AMOUNT column on two facts).
      // Model-level calculated measures (_isModelMeasure) are globally unique and
      // stay bare. The backend measure path parses "<entity_id>::<field>" and
      // emits `table.COLUMN AS "<entity_id>::COLUMN"` so they never collide.
      const _msrNameCount = {};
      fields.forEach(f => {
        if (f && f.semantic_type === 'measure' && !f._isModelMeasure && !f.is_calculated && f.entity_id && !f._sourceField) {
          _msrNameCount[f.name] = (_msrNameCount[f.name] || 0) + 1;
        }
      });
      fields.forEach(f => {
        if (f && f.semantic_type === 'measure' && !f._isModelMeasure && !f.is_calculated && f.entity_id && !f._sourceField
            && _msrNameCount[f.name] > 1 && !String(f.name).includes('::')) {
          const base = f.name;
          const baseLabel = f.business_name || f.display_name || f.label || base.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          f._baseName = base;
          f.name = f.entity_id + '::' + base;
          f.business_name = entityQualifiedLabel(f.entity_name, f.entity_logical_name, baseLabel);
          f._baseLabel = baseLabel;
        }
      });
      // Disambiguate MEASURES that still resolve to an identical display label
      // but are actually different fields. Some imports stub many amount measures
      // on a summary entity with a shared source-column label (e.g. 7 distinct
      // measures all shown as "Claim Amount"). Give each colliding measure a
      // label derived from its own field name so the picker shows them distinctly
      // instead of a wall of identical names. Legit unique labels are untouched.
      const _mLabel = (f) => f.business_name || f.display_name || f.label || f.name;
      const _mLabelCount = {};
      fields.forEach(f => { if (f && f.semantic_type === 'measure') { const l = _mLabel(f); _mLabelCount[l] = (_mLabelCount[l] || 0) + 1; } });
      fields.forEach(f => {
        if (f && f.semantic_type === 'measure' && _mLabelCount[_mLabel(f)] > 1) {
          const raw = String(f._baseName || f.name).split('::').pop();
          const pretty = raw.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          const ent = entityQualifiedLabel(f.entity_name, f.entity_logical_name, pretty);
          f.business_name = ent;
          f._baseLabel = pretty;
        }
      });
      // Merge inception-to-date calculated measures (semantic_measures) so they
      // appear in the Calculations list, grouped by their source fact.
      try {
        const itd = await api.listCalcMeasures(modelId);
        const entNameById = {};
        (m.entities || []).forEach(e => { entNameById[e.entity_id] = e.logical_name || e.name; });
        (Array.isArray(itd) ? itd : []).forEach(cm => {
          if (seen.has(cm.name)) return;
          const src = entNameById[cm.source_entity_id] || 'Calculated measures';
          fields.push({
            name: cm.name, display_name: cm.name, description: cm.description || cm.name,
            help: cm.description || '', semantic_type: 'measure', data_type: 'float',
            aggregation: cm.aggregation || 'sum', is_calculated: true, _isModelMeasure: true,
            display_folder: cm.display_folder || src, source_entity_id: cm.source_entity_id || '',
            accumulation: cm.accumulation || null,
          });
          seen.add(cm.name);
        });
      } catch { /* calc measures optional */ }
      setAllFields(prev => {
        // Only replace when fields actually changed, so a tab-refocus refresh
        // with identical fields doesn't create a new array identity and cascade
        // into a needless (costly) query re-run. Queries run on user edits only.
        try {
          if (prev.length === fields.length && JSON.stringify(prev) === JSON.stringify(fields)) return prev;
        } catch { /* fall through to replace */ }
        return fields;
      });
    } catch (err) {
      console.error('Field load error:', err);
    }
  }, [modelId]);

  useEffect(() => { loadFieldsFromModel(); }, [modelId, loadFieldsFromModel]);

  // Auto-refresh field list when the user comes back to this tab —
  // covers the flow "add a calc dim / bucket in the model editor →
  // switch back to the insight builder" so the new field shows up
  // without a manual page reload.
  useEffect(() => {
    if (!modelId) return;
    const onFocus = () => loadFieldsFromModel();
    const onVisibility = () => { if (document.visibilityState === 'visible') loadFieldsFromModel(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [modelId, loadFieldsFromModel]);

  // Guards against re-prompting for a data source more than once when several
  // visuals fail in parallel because the bound model is missing/empty.
  const modelMissingRef = React.useRef(false);
  useEffect(() => { modelMissingRef.current = false; }, [modelId]);
  // vizId -> latest request generation token (guards against stale/out-of-order
  // visual query responses overwriting good data).
  const visualQueryGenRef = React.useRef({});

  const executeVisualQuery = useCallback(async (vizId) => {
    const vis = visuals.find(v => v.id === vizId);
    if (!vis) return;
    // Per-visual request generation: overlapping/debounced effects can fire
    // several queries for the SAME visual; without this guard a slower, stale
    // (or superseded) response could resolve last and overwrite good data with
    // empty/old data — the "random visual blank on reload" bug. We stamp each
    // run with a monotonically increasing token and only commit results if this
    // run is still the latest for that visual.
    const gen = (visualQueryGenRef.current[vizId] || 0) + 1;
    visualQueryGenRef.current[vizId] = gen;
    const isLatest = () => visualQueryGenRef.current[vizId] === gen;
    // A query run (manual, resume, or auto) clears this visual's stale flag.
    setStaleVisualIds(prev => { if (!prev.has(vizId)) return prev; const n = new Set(prev); n.delete(vizId); return n; });
    // Never query an unbound data source — a brand-new dashboard (or one whose
    // draft restored before a model was picked) has no model yet.
    if (!modelId) return;
    // SQL-snapshot visuals (saved from an AI Explore raw-SQL answer) carry their
    // own data — they have no semantic dimensions, so render the stored snapshot
    // directly instead of running a semantic query (which would fail with
    // "Unknown dimension: ...").
    if (vis.sqlMode && Array.isArray(vis.sqlData)) {
      setVisualResults(prev => ({ ...prev, [vizId]: { result: { data: vis.sqlData }, pivotResult: null, triangleResult: null, comparisonResult: null, error: null } }));
      return;
    }
    setLoadingVisuals(prev => new Set([...prev, vizId]));
    try {
      // Merge dashboard-scope (global) + the active page's page-scope filters.
      const effGlobalFilterValues = { ...globalFilterValues, ...(pageFilterValues[activePageId] || {}) };
      const effGlobalFilterDims = [...(globalFilterDims || []), ...((pageFilterDims[activePageId]) || [])];
      const { result, pivotResult, triangleResult, comparisonResult, queryInfo, error } = await runVisualQuery({
        vis, allFields, modelId,
        slicerSelections, globalFilterValues: effGlobalFilterValues, globalFilterDims: effGlobalFilterDims, crossFilter, drillFilters,
        markColor, markDetail, visualConfigs,
        safeEvalMath,
        asOfDate: vis.asOfDate || effGlobalFilterValues['__asof__'] || undefined,
      });
      // Discard superseded responses: a newer run for this visual started while
      // we were awaiting the network, so this result is stale — do not commit.
      if (!isLatest()) return;
      setVisualResults(prev => ({ ...prev, [vizId]: { result, pivotResult, triangleResult, comparisonResult, error: error?.message || null } }));
      if (error) {
        const msg = error.message || '';
        // A model-level failure (deleted / re-pointed / empty data source, or a
        // stale draft pointing at a model with no entities) shouldn't spam a raw
        // query error on a new dashboard — re-prompt for a data source instead.
        if (/no entities found for model/i.test(msg)) {
          if (!modelMissingRef.current) {
            modelMissingRef.current = true;
            setModelChosen(false);
            // Only warn when an EXISTING saved report's source went missing; a
            // brand-new/unsaved dashboard should just show the data-source picker.
            if (reportId) toast.error('That data source is no longer available. Please choose a data source to continue.');
          }
        } else {
          toast.error(`Query failed for "${vis.title || vizId}": ${msg}`);
        }
      }
      if (queryInfo) setVisualQueryInfo(prev => ({ ...prev, [vizId]: queryInfo }));
    } catch {}
    if (isLatest()) {
      setLoadingVisuals(prev => { const next = new Set(prev); next.delete(vizId); return next; });
    }
  }, [visuals, slicerSelections, globalFilterValues, pageFilterValues, activePageId, crossFilter, drillFilters, modelId, markColor, markDetail, visualConfigs, allFields]);

  const executeQuery = useCallback(async () => {
    if (!modelId) return;
    setLoading(true);
    try {
      // Execute queries for ALL visuals in parallel (staggered)
      const dataVisuals = visuals.filter(v => !['ai_text', 'rich_text', 'image', 'shape'].includes(v.vizType));
      await Promise.all(dataVisuals.map(vis => executeVisualQuery(vis.id)));
      // Per-visual slicer values are loaded by a dedicated effect (see below)
      // so slicers added AFTER the initial query still populate.
      // Load global slicer values
      for (const dim of globalFilterDims) {
        if (!globalSlicerValues[dim]) {
          try {
            const sv = await api.executeQuery({ model_id: modelId, dimensions: [dim], measures: [], limit: 500 });
            const uniqueVals = [...new Set((sv.data || []).map(r => r[dim]).filter(Boolean))].sort();
            setGlobalSlicerValues(prev => ({ ...prev, [dim]: uniqueVals }));
          } catch {}
        }
      }
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [executeVisualQuery, activeVisual, modelId, globalFilterDims]);

  // Re-execute ALL visuals when global filter or cross-filter changes
  useEffect(() => {
    if (!modelId) return;
    const t = setTimeout(() => {
      visualsRef.current.forEach(v => executeVisualQuery(v.id));
    }, 300);
    return () => clearTimeout(t);
  }, [globalFilterValues, crossFilter]);

  // Cascading slicers: build the filters from every OTHER slicer's current
  // selection (in-tile + page-level), so loading dim D's distinct values only
  // returns values that co-occur with the other picks (e.g. pick a State → the
  // Agent slicer only lists that state's agents). Date slicers don't contribute
  // a value list but their date filter still narrows the others.
  const buildCascadeFilters = useCallback((excludeField) => {
    if (!cascadeSlicers) return [];
    const selByField = {};
    (visualsRef.current || []).forEach(v => (v.filterDims || []).forEach(dim => {
      const val = slicerSelections[`${v.id}_${dim}`];
      if (val) selByField[dim] = val;
    }));
    Object.entries(globalFilterValues || {}).forEach(([f, val]) => {
      if (val && !f.startsWith('__')) selByField[f] = val;
    });
    const out = [];
    Object.entries(selByField).forEach(([f, val]) => {
      if (f === excludeField) return;
      out.push(...filtersForSelection(f, val));
    });
    return out;
  }, [slicerSelections, globalFilterValues, cascadeSlicers]);

  // Load distinct values for EVERY per-visual slicer dimension. Re-runs whenever
  // any slicer selection changes so cascading stays live. Skips date dims (they
  // render a calendar, not a value list).
  const allFilterDimsSig = useMemo(
    () => JSON.stringify([...new Set((visuals || []).flatMap(v => v.filterDims || []))].sort()),
    [visuals]
  );
  const slicerSelSig = useMemo(() => JSON.stringify(slicerSelections), [slicerSelections]);
  const globalValSig = useMemo(() => JSON.stringify(globalFilterValues), [globalFilterValues]);
  useEffect(() => {
    if (!modelId) return;
    const dims = [...new Set((visualsRef.current || []).flatMap(v => v.filterDims || []))]
      .filter(dim => !isDateField(allFields.find(f => f.name === dim)));
    if (!dims.length) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      for (const dim of dims) {
        try {
          const filters = buildCascadeFilters(dim);
          const sv = await api.executeQuery({ model_id: modelId, dimensions: [dim], measures: [], filters, limit: 500 });
          if (cancelled) return;
          const uniqueVals = [...new Set((sv.data || []).map(r => r[dim]).filter(Boolean))].sort();
          setSlicerValues(prev => ({ ...prev, [dim]: uniqueVals }));
        } catch {}
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFilterDimsSig, modelId, slicerSelSig, globalValSig, cascadeSlicers]);

  // Same idea for PAGE-LEVEL filter dims (dashboard-scope + active page-scope), cascade-aware.
  const globalFilterDimsSig = useMemo(() => JSON.stringify([...(globalFilterDims || []), ...((pageFilterDims[activePageId]) || [])].sort()), [globalFilterDims, pageFilterDims, activePageId]);
  useEffect(() => {
    if (!modelId) return;
    const allDims = [...new Set([...(globalFilterDims || []), ...((pageFilterDims[activePageId]) || [])])];
    const dims = allDims.filter(dim => !isDateField(allFields.find(f => f.name === dim)));
    if (!dims.length) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      for (const dim of dims) {
        try {
          const filters = buildCascadeFilters(dim);
          const sv = await api.executeQuery({ model_id: modelId, dimensions: [dim], measures: [], filters, limit: 500 });
          if (cancelled) return;
          const uniqueVals = [...new Set((sv.data || []).map(r => r[dim]).filter(Boolean))].sort();
          setGlobalSlicerValues(prev => ({ ...prev, [dim]: uniqueVals }));
        } catch {}
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilterDimsSig, modelId, slicerSelSig, globalValSig, cascadeSlicers]);


  // Initial query execution (only on model change, not on every visual text edit)
  const [initialQueryDone, setInitialQueryDone] = useState(false);
  useEffect(() => {
    if (!modelId || initialQueryDone) return;
    const t = setTimeout(() => { executeQuery(); setInitialQueryDone(true); }, 300);
    return () => clearTimeout(t);
  }, [modelId]);

  // Per-visual signature of ONLY the query-driving fields. Editing a title (or
  // any purely cosmetic prop) must NOT change a signature, so it never reruns.
  const visualQuerySignatures = useMemo(() => {
    const map = {};
    (visuals || []).forEach(v => {
      map[v.id] = JSON.stringify({
        vizType: v.vizType, rowDims: v.rowDims, colDim: v.colDim,
        valueMeasures: v.valueMeasures, filterDims: v.filterDims,
        asOfDate: v.asOfDate, dimEntities: v.dimEntities, sqlMode: v.sqlMode,
      });
    });
    return map;
  }, [visuals]);
  const prevVisualSigsRef = useRef(null);
  const pendingRerunIdsRef = useRef(new Set());
  const executeVisualQueryRef = useRef(executeVisualQuery);
  executeVisualQueryRef.current = executeVisualQuery;
  const autoRunPausedRef = useRef(autoRunPaused);
  autoRunPausedRef.current = autoRunPaused;

  // Rerun ONLY the visual(s) whose data/shape changed (fields, chart type,
  // as-of, entity pins) — not title/cosmetic edits, and not every visual on the
  // page. Rapid edits accumulate and flush as ONE debounced batch. When
  // auto-run is PAUSED, changed visuals are marked stale instead of re-querying.
  useEffect(() => {
    if (!modelId || !initialQueryDone) return;
    const prev = prevVisualSigsRef.current;
    const cur = visualQuerySignatures;
    const changedIds = [];
    if (prev) {
      Object.keys(cur).forEach(id => {
        if (prev[id] === cur[id]) return;
        const v = visualsRef.current.find(vv => vv.id === id);
        if (v && (v.rowDims?.length > 0 || v.valueMeasures?.length > 0) &&
            !['ai_text', 'rich_text', 'image', 'shape'].includes(v.vizType)) {
          changedIds.push(id);
        }
      });
    }
    prevVisualSigsRef.current = cur;
    if (!changedIds.length) return;
    if (autoRunPausedRef.current) {
      setStaleVisualIds(prevSet => {
        const next = new Set(prevSet);
        changedIds.forEach(id => next.add(id));
        return next;
      });
      return;
    }
    changedIds.forEach(id => pendingRerunIdsRef.current.add(id));
    const t = setTimeout(() => {
      const ids = Array.from(pendingRerunIdsRef.current);
      pendingRerunIdsRef.current = new Set();
      ids.forEach(id => executeVisualQueryRef.current(id));
    }, 250);
    return () => clearTimeout(t);
  }, [visualQuerySignatures, initialQueryDone, modelId]);

  // Run every visual that went stale while paused (each run clears its flag).
  const runStaleVisuals = useCallback(() => {
    Array.from(staleVisualIds).forEach(id => executeVisualQueryRef.current(id));
  }, [staleVisualIds]);

  // Flip Live/Paused. Resuming (→ Live) immediately runs any pending stale visuals.
  const toggleAutoRunPaused = useCallback(() => {
    setAutoRunPaused(prev => {
      const next = !prev;
      if (!next) {
        setStaleVisualIds(cur => {
          Array.from(cur).forEach(id => executeVisualQueryRef.current(id));
          return new Set();
        });
      }
      return next;
    });
  }, []);

  // Keyboard shortcut: ⌘/Ctrl+Enter runs pending stale visuals while paused.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        if (autoRunPausedRef.current && staleVisualIds.size > 0) {
          e.preventDefault();
          runStaleVisuals();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [runStaleVisuals, staleVisualIds]);

  // Rerun the affected visual(s) when a per-visual slicer SELECTION changes.
  // Slicer picks live in `slicerSelections` keyed `<vizId>_<dim>`, but nothing
  // else watches them — so without this, choosing a slicer value updated state
  // but never re-queried the visual (chart stayed unfiltered).
  const prevSlicerSelRef = useRef(slicerSelections);
  useEffect(() => {
    if (!modelId || !initialQueryDone) return;
    const prev = prevSlicerSelRef.current || {};
    const cur = slicerSelections || {};
    const changedKeys = [...new Set([...Object.keys(prev), ...Object.keys(cur)])].filter(k => prev[k] !== cur[k]);
    prevSlicerSelRef.current = cur;
    if (!changedKeys.length) return;
    const vids = new Set();
    (visualsRef.current || []).forEach(v => {
      (v.filterDims || []).forEach(fd => { if (changedKeys.includes(v.id + '_' + fd)) vids.add(v.id); });
    });
    if (!vids.size) return;
    const t = setTimeout(() => { vids.forEach(id => executeVisualQueryRef.current(id)); }, 250);
    return () => clearTimeout(t);
  }, [slicerSelections, initialQueryDone, modelId]);


  // Auto-rerun AI text blocks when filters or cross-filter change
  const rerunAiBlocks = useCallback(async (trigger) => {
    const aiVisuals = visuals.filter(v => v.vizType === 'ai_text' && (v.aiRerunOnFilter !== false || v.aiRerunOnClick));
    if (!aiVisuals.length) return;
    const otherVisuals = visuals.filter(v => v.vizType !== 'ai_text');
    let sampleData = [];
    otherVisuals.forEach(v => { const vr = visualResults[v.id]; if (vr?.result?.data) sampleData = sampleData.concat(vr.result.data.slice(0, 10)); });
    if (!sampleData.length) return;

    // Build filter context description for AI
    const filterParts = [];
    if (crossFilter) {
      filterParts.push(`Cross-filtered by ${crossFilter.field.replace(/_/g, ' ')} = "${crossFilter.value}"`);
    }
    Object.entries(globalFilterValues).forEach(([k, v]) => {
      if (!v) return;
      if (k === '__daterange__' && v.preset) {
        filterParts.push(`Date range: ${String(v.preset).replace(/_/g, ' ')}`);
      } else if (k === '__asof__') {
        filterParts.push(`As of ${v}`);
      } else if (!k.startsWith('__') && typeof v !== 'object') {
        filterParts.push(`Global filter: ${k.replace(/_/g, ' ')} = "${v}"`);
      }
    });
    Object.entries(slicerSelections).forEach(([k, v]) => {
      if (v) filterParts.push(`Slicer: ${k.replace(/_/g, ' ')} = "${v}"`);
    });
    const filterContext = filterParts.length > 0
      ? `\n\nIMPORTANT: The data is currently filtered. Active filters: ${filterParts.join('; ')}. Acknowledge these filters in your analysis and explain how they affect the data shown.`
      : '';

    for (const v of aiVisuals) {
      try {
        setLoadingAiVisuals(prev => new Set([...prev, v.id]));
        const fullPrompt = (v.aiPrompt || reportName) + filterContext;
        const n = await api.generateNarrative(sampleData.slice(0, 20), rowDims, valueMeasures, fullPrompt);
        const content = (n.narrative || '') + (n.highlights ? '\n\n' + n.highlights.map(h => '\u2022 ' + h).join('\n') : '');
        setAiContents(prev => ({ ...prev, [v.id]: content }));
      } catch {}
      finally { setLoadingAiVisuals(prev => { const next = new Set(prev); next.delete(v.id); return next; }); }
    }
  }, [visuals, visualResults, rowDims, valueMeasures, reportName, crossFilter, globalFilterValues, slicerSelections]);

  // Rerun AI text blocks when cross-filter or global filters change (with delay for queries to complete)
  const aiRerunTimer = React.useRef(null);
  useEffect(() => {
    if (aiRerunTimer.current) clearTimeout(aiRerunTimer.current);
    aiRerunTimer.current = setTimeout(() => rerunAiBlocks('filter'), 2000);
    return () => { if (aiRerunTimer.current) clearTimeout(aiRerunTimer.current); };
  }, [crossFilter, globalFilterValues, slicerSelections]);


  // Keyboard shortcuts: Undo / Redo / Save — registered with the global shortcut registry
  // so they appear in the "?" help dialog and don't fight other features for the keydown event.
  useUndoRedoKeyboard(undo, redo);
  useGlobalShortcut({
    id: 'report-builder.refresh-data',
    keys: 'r',
    label: 'Refresh data (rerun all queries)',
    category: 'Data',
    handler: () => { if (modelId) executeQuery(); },
  });
  useGlobalShortcut({
    id: 'report-builder.save',
    keys: 'mod+s',
    label: 'Save report',
    category: 'Editing',
    handler: () => { if (reportName.trim()) setSaveDialogOpen(true); },
  });

  // Auto-refresh timer
  useEffect(() => {
    if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    if (autoRefreshInterval > 0 && modelId) {
      autoRefreshRef.current = setInterval(() => { executeQuery(); }, autoRefreshInterval * 1000);
    }
    return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current); };
  }, [autoRefreshInterval, modelId]);

  // Auto-refresh pinned correlations when filters change
  const corrRerunTimer = React.useRef(null);
  useEffect(() => {
    if (corrRerunTimer.current) clearTimeout(corrRerunTimer.current);
    corrRerunTimer.current = setTimeout(async () => {
      // Find visuals with pinned correlations
      for (const v of visuals) {
        const cfg = getVizConfig(v.id);
        if (cfg?.pinnedCorrelation && v.valueMeasures?.length > 0) {
          const vData = getTransformedData(v.id);
          const vResult = (visualResults[v.id] || {}).result;
          const dataToSend = vData || vResult?.data;
          if (!dataToSend?.length) continue;
          try {
            const dims = allFields.filter(f => f.semantic_type === 'dimension').map(f => f.name);
            const measures = allFields.filter(f => f.semantic_type === 'measure').map(f => f.name);
            const res = await api.correlationAnalysis({
              data: dataToSend.slice(0, 60),
              target_measure: v.valueMeasures[0],
              dimensions: dims,
              measures,
            });
            if (res?.result) setVizConfig(v.id, { pinnedCorrelation: res.result });
          } catch (err) {
            console.warn('Correlation auto-refresh failed for', v.id, err);
          }
        }
      }
    }, 3000);
    return () => { if (corrRerunTimer.current) clearTimeout(corrRerunTimer.current); };
  }, [crossFilter, globalFilterValues, slicerSelections, visualResults]);


  const handleDrill = (val) => {
    const avail = allFields.filter(f => f.semantic_type === 'dimension' && !rowDims.includes(f.name) && f.name !== colDim);
    if (!avail.length) return;
    setDrillPath(p => [...p, { dim: rowDims[0], value: val }]);
    setDrillFilters(p => [...p, { field: rowDims[0], operator: 'equals', value: val }]);
    setRowDims([avail[0].name]);
  };

  const handleCrossFilter = (val, vizId) => {
    const vis = visuals.find(v => v.id === vizId);
    if (!vis || !vis.rowDims?.length) return;
    const field = vis.rowDims[0];
    const vizCfg = getVizConfig(vizId);
    // Drill-through: navigate to a target report filtered by the clicked value
    if (vizCfg?.drillThrough?.enabled && vizCfg.drillThrough.targetReportId) {
      const targetId = vizCfg.drillThrough.targetReportId;
      const filterParam = encodeURIComponent(JSON.stringify({ field, value: val }));
      if (isTabbed && onOpenReport) {
        onOpenReport(targetId);
      } else {
        navigate(`/dashboards/view?id=${targetId}&drillFilter=${filterParam}`);
      }
      return;
    }
    // Cross-filter
    if (crossFilter && crossFilter.field === field && crossFilter.value === val) {
      setCrossFilter(null);
    } else {
      setCrossFilter({ field, value: val, sourceVisualId: vizId });
    }
  };
  const drillUp = () => { if (!drillPath.length) return; const p = drillPath[drillPath.length - 1]; setDrillPath(pp => pp.slice(0, -1)); setDrillFilters(pp => pp.slice(0, -1)); setRowDims([p.dim]); };

  const [vizPlacementOpen, setVizPlacementOpen] = useState(false);
  const [pendingVizId, setPendingVizId] = useState(null);

  // Load library visuals
  useEffect(() => {
    api.getVisuals().then(vs => setLibraryVisuals(vs || [])).catch(() => {});
  }, []);

  const addLibraryVisual = (lv) => {
    const id = genId('v');
    const newViz = {
      id, vizType: lv.viz_type || 'bar_chart', title: lv.name || 'Library Visual',
      rowDims: lv.row_dims || [], colDim: lv.col_dim || '',
      valueMeasures: lv.value_measures || [], filterDims: lv.filter_dims || [],
      library_visual_id: lv.visual_id, // Link to library
    };
    setVisuals(prev => [...prev, newViz]);
    visualsRef.current = [...visualsRef.current, newViz];
    setActiveVisual(id);
    // Apply vizConfig from library
    if (lv.viz_config && Object.keys(lv.viz_config).length > 0) {
      setVizConfig(id, lv.viz_config);
    }
    // Set model if not already set
    if (lv.model_id && !modelId) {
      setModelId(lv.model_id);
    }
    setSavedClean(false);
    toast.success(`Added "${lv.name}" from library`);
  };

  const addVisual = (forceZone) => {
    emitCoachEvent('widget_added', 'report-visual-canvas');
    pushUndo('Added visual');
    const id = genId('v');
    const newViz = { 
      id, 
      vizType: 'bar_chart', 
      title: `Visual ${visuals.length + 1}`, 
      rowDims: [], 
      colDim: '', 
      valueMeasures: [], 
      filterDims: [],
      // Assign to current report page when in report mode
      _reportPageIndex: designerMode === 'report' ? activeReportPageIndex : undefined,
    };
    if (designerMode === 'report' && !forceZone) {
      // In report mode, ask where to place it
      setPendingVizId(id);
      setVisuals(p => [...p, { ...newViz, _zone: undefined }]); // add temporarily
      setActiveVisual(id);
      setVizPlacementOpen(true);
    } else {
      if (forceZone) newViz._zone = forceZone;
      setVisuals(p => [...p, newViz]);
      setActiveVisual(id);
    }
  };

  const confirmVizPlacement = (zone) => {
    if (pendingVizId) {
      const zoneVal = zone === 'body' ? undefined : zone;
      updateVisual(pendingVizId, { _zone: zoneVal, _reportPageIndex: activeReportPageIndex });
    }
    setVizPlacementOpen(false);
    setPendingVizId(null);
  };
  const updateVisual = (id, u, explicitLabel) => {
    const _viz = visuals.find(v => v.id === id);
    const _name = _viz?.title || 'visual';
    let _label = `Edited ${_name}`;
    if (u) {
      if (u.vizType) _label = `Changed ${_name} to ${String(u.vizType).replace(/_/g, ' ')}`;
      else if (u.title !== undefined) _label = `Renamed visual`;
      else if (u.freePosition) _label = `Moved/resized ${_name}`;
      else if (u.rowDims || u.colDim !== undefined || u.valueMeasures) _label = `Changed ${_name} fields`;
      else if (u.filterDims) _label = `Changed ${_name} filters`;
    }
    pushUndo(explicitLabel || _label);
    setVisuals(p => p.map(v => v.id === id ? { ...v, ...u } : v));
    // If the visual type is changing to a plugin-registered viz type,
    // seed visualConfigs with its defaultConfig (without clobbering user-set keys).
    if (u && u.vizType) {
      const reg = getRegisteredVizType(u.vizType);
      const defaults = reg?.meta?.defaultConfig;
      if (defaults && Object.keys(defaults).length) {
        setVisualConfigs(prev => ({
          ...prev,
          [id]: { ...defaults, ...(prev[id] || {}) },
        }));
      }
    }
  };
  const {
    getMaxZIndex,
    getMinZIndex,
    handleVisualContextMenu,
    handleZIndexChange,
    handleDeleteFromCtx,
    handleDuplicateFromCtx,
    handleCreateAlertFromViz,
  } = useVisualContextMenuActions({
    visuals, setVisuals, activeVisual, setActiveVisual,
    visualResults, allFields,
    visualCtxMenu, setVisualCtxMenu,
    updateVisual, setSavedClean, pushUndo,
    setAlertFromVizData, setAlertFromVizOpen,
  });
  const freeCanvasMinHeight = visuals.reduce((max, v) =>
    isObjectVisual(v.vizType) ? Math.max(max, (v.freePosition?.y || 0) + (v.freePosition?.h || 150) + 50) : max, 0);
  const removeVisual = (id) => {
    if (visuals.length <= 1) return;
    confirmFn({
      title: 'Remove visual?',
      message: 'This visual will be deleted from the report. This cannot be undone.',
      confirmLabel: 'Remove',
      destructive: true,
    }).then(ok => {
      if (!ok) return;
      setSavedClean(false);
      setVisuals(p => p.filter(v => v.id !== id));
      if (activeVisual === id) setActiveVisual(visuals[0]?.id);
    });
  };

  // Bundle all in-memory builder state into the single object consumed by
  // persistReport/cloneCurrentReport. Any new field added to the builder
  // just needs to be added here and to reportPersistence.buildReportPayload.
  const buildBuilderState = () => ({
    reportName, reportDesc, modelId, visuals, showGrid, showLegend,
    refLineValue, refLineLabel, condRules, secondYAxisConfig, dateParts,
    reportTheme, gridLayouts, parameters, paramValues, globalFilterPosition,
    visualConfigs, fieldOverrides, pages, pageVisuals, pageNavStyle, aiContents, annotations,
    mobileLayout, designerMode, reportPageSize, reportOrientation, reportMargins,
    reportHeader, reportFooter, reportTableSpanPages, reportTableRepeatHeader, reportPages,
    // Slicer/filter selections & display modes so a reopened insight remembers
    // the chosen date-slicer grain/periods/presets (in-tile + page-level).
    slicerSelections, globalFilterValues, globalFilterDims, slicerDisplayModes, slicerSettings, cascadeSlicers,
    pageFilterDims, pageFilterValues,
    importSessionId,
  });

  // Snapshot the in-progress draft (debounced) for a NEW insight tab so it
  // survives navigating away and back. Saved insights reload from the server,
  // so we skip them.
  useEffect(() => {
    if (editReportId || !draftKey || !modelChosen) return;
    const t = setTimeout(() => {
      try {
        const snap = { ...buildBuilderState(), pageVisuals, slicerSelections, globalFilterValues, activePageId, activeVisual, modelChosen: true };
        const payload = JSON.stringify(snap);
        sessionStorage.setItem(draftKey, payload);
        // Draft tabs own a unique, URL-backed key; only legacy non-draft tabs
        // use the shared singleton (avoids cross-contaminating other new tabs).
        if (!(tabId || '').startsWith('draft:')) sessionStorage.setItem('rb_draft_new_singleton', payload);
      } catch (_) { /* serialization / quota — ignore */ }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editReportId, draftKey, modelChosen, modelId, reportName, reportDesc, pageVisuals, visualConfigs, gridLayouts, reportPages, pages, activePageId, activeVisual, slicerSelections, globalFilterValues, parameters, paramValues]);

  // Restore recovered unsaved edits for a SAVED report AFTER the server load
  // finishes — so an accidental refresh keeps in-progress layout changes.
  useEffect(() => {
    if (!serverLoadDone || !recoveryKey || recoveryHandledRef.current) return;
    recoveryHandledRef.current = true;
    let raw = null;
    try { raw = sessionStorage.getItem(recoveryKey); } catch (_) { raw = null; }
    if (!raw) return;
    let snap = null;
    try { snap = JSON.parse(raw); } catch (_) { return; }
    if (!snap || !snap.__recovery) return;
    applyBuilderState(snap);
    setSavedClean(false);
    toast('Restored your unsaved changes', {
      description: 'We recovered layout edits from before the page reloaded.',
      duration: 12000,
      action: {
        label: 'Discard',
        onClick: () => {
          try { sessionStorage.removeItem(recoveryKey); } catch (_) { /* ignore */ }
          window.location.reload();
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverLoadDone, recoveryKey]);

  // Silently autosave a SAVED report's in-progress edits to sessionStorage
  // (debounced) whenever there are unsaved changes; clear the snapshot once the
  // report is clean (saved or nothing pending).
  useEffect(() => {
    if (!editReportId || !recoveryKey || !serverLoadDone) return;
    if (savedClean) {
      try { sessionStorage.removeItem(recoveryKey); } catch (_) { /* ignore */ }
      setDraftSavedAt(0);
      return;
    }
    const t = setTimeout(() => {
      if (savingRef.current) return;
      try {
        const snap = { ...buildBuilderState(), pageVisuals, slicerSelections, globalFilterValues, activePageId, activeVisual, modelChosen: true, __recovery: true, __at: Date.now() };
        sessionStorage.setItem(recoveryKey, JSON.stringify(snap));
        setDraftSavedAt(Date.now());
      } catch (_) { /* serialization / quota — ignore */ }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editReportId, recoveryKey, serverLoadDone, savedClean, modelId, reportName, reportDesc, pageVisuals, visualConfigs, gridLayouts, reportPages, pages, activePageId, activeVisual, slicerSelections, globalFilterValues, parameters, paramValues, visuals, reportTheme, annotations]);

  // ── Phase 3: self-hosted co-editing for saved reports (mirror of the
  // Dashboard Builder). Single-writer snapshot sync + shared cursors + control. ──
  const me = user?.user_id;
  const coRoom = editReportId ? `report:${editReportId}` : null;
  const [coEnabled, setCoEnabled] = useState(true);
  const [barPos, setBarPos] = useState(null);          // {x,y} viewport coords once dragged
  const barDrag = useRef(null);
  const barElRef = useRef(null);
  const applyCoSnapshot = useCallback((s) => {
    if (s.pageVisuals) setPageVisuals(s.pageVisuals);
    if (s.visualConfigs) setVisualConfigs(s.visualConfigs);
    if (s.fieldOverrides) setFieldOverrides(s.fieldOverrides);
    if (s.reportPages) setReportPages(s.reportPages);
    if (s.pages) setPages(s.pages);
    if (s.parameters) setParameters(s.parameters);
    if (s.reportName !== undefined) setReportName(s.reportName);
    if (s.reportDesc !== undefined) setReportDesc(s.reportDesc);
  }, []);
  const co = useCoEdit({
    room: (coEnabled ? coRoom : null), me,
    snapshot: { pageVisuals, visualConfigs, fieldOverrides, reportPages, pages, parameters, reportName, reportDesc },
    applySnapshot: applyCoSnapshot,
  });
  const amEditor = co.amEditor;

  // Drag the collaboration panel anywhere on the screen (viewport-fixed).
  const startBarDrag = (e) => {
    e.preventDefault();
    const el = barElRef.current;
    const r = el?.getBoundingClientRect();
    const base = barPos || (r ? { x: r.left, y: r.top } : { x: window.innerWidth - 340, y: window.innerHeight - 80 });
    barDrag.current = { sx: e.clientX, sy: e.clientY, bx: base.x, by: base.y };
    const move = (ev) => {
      const d = barDrag.current; if (!d) return;
      const w = el?.offsetWidth || 300, h = el?.offsetHeight || 40;
      const nx = Math.max(0, Math.min(window.innerWidth - w, d.bx + (ev.clientX - d.sx)));
      const ny = Math.max(0, Math.min(window.innerHeight - h, d.by + (ev.clientY - d.sy)));
      setBarPos({ x: nx, y: ny });
    };
    const up = () => { barDrag.current = null; window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };
  const [builderShareOpen, setBuilderShareOpen] = useState(false);
  const doShareBuilder = async (target) => {
    setBuilderShareOpen(false);
    try {
      const route = `${window.location.pathname}?id=${editReportId}`;
      const attachment = { type: 'shared_view', route, label: reportName || 'this report', object_type: 'report', object_id: editReportId };
      if (target?.user_id) {
        const conv = await api.createConversation({ type: 'dm', participant_ids: [target.user_id] });
        await api.sendMessage(conv.conversation_id, { text: `${user?.name || 'Someone'} invited you to co-edit`, attachment });
        toast.success(`Invited ${target.name} to co-edit`, { action: { label: 'Open chat', onClick: () => navigate(`/threads?conversation=${conv.conversation_id}`) } });
      } else {
        const conv = await api.getObjectConversation('report', editReportId, reportName);
        await api.sendMessage(conv.conversation_id, { text: `${user?.name || 'Someone'} invited the team to co-edit`, attachment });
        toast.success("Invite posted to this report's chat");
      }
    } catch (e) { toast.error(e.message); }
  };

  const saveReport = async (versionDesc) => {
    if (!amEditor) { toast.error('Ask for edit control first'); return; }
    if (!reportName.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    savingRef.current = true;
    try {
      const { reportId: rid } = await persistReport({ state: buildBuilderState(), reportId, versionDesc });
      // Every save increments the report's _version by 1 (new reports start at
      // v1). Update optimistically — a re-fetch can be served stale by the
      // service worker's stale-while-revalidate cache.
      setReportVersion(prev => (prev == null ? 1 : prev + 1));
      if (draftKey) { try { sessionStorage.removeItem(draftKey); sessionStorage.removeItem('rb_draft_new_singleton'); } catch (_) { /* ignore */ } }
      if (recoveryKey) { try { sessionStorage.removeItem(recoveryKey); } catch (_) { /* ignore */ } }
      if (!reportId) {
        setReportId(rid);
        if (onReportIdChange) onReportIdChange(rid);
        // Reflect the saved name on the tab immediately so the Workbench chip
        // reads the real name (not "Untitled insight") right after first save.
        if (onLabelChange) onLabelChange(reportName || 'Untitled');
        // Write ?id=<rid> into the URL so this becomes a durable session:
        // returning to its Workbench chip re-loads the saved insight instead
        // of remounting a blank builder. Retire the transient "new" chip.
        try {
          const base = window.location.pathname;
          navigate(`${base}?id=${rid}`, { replace: true });
          if (ws) {
            ws.closeTab('insight:new');
            if ((tabId || '').startsWith('draft:')) ws.closeTab(`insight:draft:${tabId.slice(6)}`);
          }
        } catch (_) { /* non-fatal */ }
      }
      setSaveDialogOpen(false);
      setSavedClean(true);
      toast.success('Report saved & versioned');
      // Imported-from-file reports: offer to reconcile against the source file
      // right after the first save (the linkage rides in config.import_session_id).
      if (!reportId && importSessionId) {
        toast('Compare against your source file?', {
          description: 'Check how the report totals line up with your uploaded spreadsheet.',
          action: { label: 'Compare', onClick: () => navigate(`/reports/view?id=${rid}&compareSource=1`) },
          duration: 12000,
        });
      }
      // Thumbnail capture (html2canvas) blocks the main thread, so run it only
      // when the browser is idle AFTER the save completes and upload it in the
      // background. The save itself stays instant and the user is never frozen
      // mid-interaction waiting on a cosmetic snapshot.
      const runThumb = () => {
        captureCanvasThumbnail(canvasRef.current)
          .then(thumb => { if (thumb) return api.saveReportThumbnail(rid, thumb); })
          .catch(() => { /* non-fatal — thumbnail is cosmetic */ });
      };
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(runThumb, { timeout: 5000 });
      } else {
        setTimeout(runThumb, 1000);
      }
    } catch (err) { toast.error(err.message); } finally { setSaving(false); savingRef.current = false; }
  };

  const saveAs = () => {
    setSaveAsName('Copy of ' + (reportName || 'Untitled'));
    setSaveAsOpen(true);
  };
  const doSaveAs = async () => {
    const nm = (saveAsName || '').trim();
    if (!nm) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const state = { ...buildBuilderState(), reportName: nm };
      const { reportId: rid } = await persistReport({ state, reportId: null });
      setSaveAsOpen(false);
      toast.success('Saved as "' + nm + '"');
      navigate(`/dashboards/builder?id=${rid}`);
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const cloneReport = async () => {
    if (!reportName.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const newId = await cloneCurrentReport({ state: buildBuilderState() });
      toast.success('Report cloned as "' + reportName + ' (Copy)"');
      navigate(`/dashboards/builder?id=${newId}`);
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  // Resolve parameter references in values (e.g., "$Target" -> paramValues["Target"])
  const currentVisual = visuals.find(v => v.id === activeVisual) || visuals[0];
  const resolveParam = (val) => {
    if (typeof val === 'string' && val.startsWith('$')) {
      const pName = val.substring(1);
      return paramValues[pName] || val;
    }
    return val;
  };
  const resolvedRefLineValue = resolveParam(refLineValue);

  // ─── Data transformations (delegated to extracted module) ───
  const getTransformedData = (vizId) => computeTransformedData({
    vizId, visualResults, visuals, dateParts, pageField, pageValues, pageIndex, tableCalcs, visualConfigs,
    otherExpanded: otherExpanded[vizId],
  });

  const transformedData = getTransformedData(activeVisual);

  const dimensions = allFields.filter(f => f.semantic_type === 'dimension');
  const fieldMeasures = allFields.filter(f => f.semantic_type === 'measure' && !f._isModelMeasure);
  const calcMeasureFields = allFields.filter(f => f._isModelMeasure);
  const filteredDims = fieldSearch ? dimensions.filter(f => f.name.includes(fieldSearch) || (f.description || '').toLowerCase().includes(fieldSearch.toLowerCase()) || (f.business_name || '').toLowerCase().includes(fieldSearch.toLowerCase())) : dimensions;
  const filteredMsrs = fieldSearch ? fieldMeasures.filter(f => f.name.includes(fieldSearch) || (f.description || '').toLowerCase().includes(fieldSearch.toLowerCase()) || (f.business_name || '').toLowerCase().includes(fieldSearch.toLowerCase())) : fieldMeasures;
  const filteredCalcMsrs = fieldSearch ? calcMeasureFields.filter(f => f.name.includes(fieldSearch) || (f.description || '').toLowerCase().includes(fieldSearch.toLowerCase()) || (f.business_name || '').toLowerCase().includes(fieldSearch.toLowerCase())) : calcMeasureFields;

  // ─── Auto Map Switch: when a Geographic field is added to a visual, switch
  // it to a Geo Map (unless the user has explicitly locked a chart type). ───
  const GEO_CATEGORIES = ['geo_state', 'geo_county', 'geo_city', 'geo_postal', 'geo_latitude', 'geo_longitude'];
  const maybeAutoMapSwitch = (fieldName) => {
    const field = allFields.find(f => f.name === fieldName);
    const cat = field?.data_category;
    if (!cat || !GEO_CATEGORIES.includes(cat)) return;
    const viz = visuals.find(v => v.id === activeVisual);
    if (!viz || viz._vizTypeLocked || viz.vizType === 'geo_map') return;
    updateVisual(activeVisual, { vizType: 'geo_map' }, `Auto-switched ${viz.title || 'visual'} to Geo Map`);
    // Seed a sensible geo config from the category.
    const isLatLong = cat === 'geo_latitude' || cat === 'geo_longitude';
    setVizConfig(activeVisual, {
      geoMode: isLatLong ? 'points' : 'choropleth',
      geoScope: cat === 'geo_state' ? 'us-states' : 'auto',
    });
    toast.success('Switched to Geo Map for the geographic field');
  };

  // Handler to add field to active visual via click/double-click
  const handleAddFieldToVisual = (fieldName, fieldType, entityId) => {
    const currentViz = visuals.find(v => v.id === activeVisual) || visuals[0];
    
    if (!activeVisual || !currentViz) {
      toast.info('Select a visual first to add fields');
      return;
    }
    
    if (fieldType === 'dimension') {
      // Add to rowDims (CATEGORY axis)
      if (!currentViz.rowDims?.includes(fieldName)) {
        updateVisual(activeVisual, {
          rowDims: [...(currentViz.rowDims || []), fieldName],
          dimEntities: entityId ? { ...(currentViz.dimEntities || {}), [fieldName]: entityId } : currentViz.dimEntities,
        }, `Added "${fieldName}" to Category`);
        toast.success(`Added "${fieldName}" to Category axis`);
        maybeAutoMapSwitch(fieldName);
        emitCoachEvent('dimension_added', 'report-dimension-shelf');
      } else {
        toast.info(`"${fieldName}" is already in the visual`);
      }
    } else {
      // Add to valueMeasures (VALUES axis)
      if (!currentViz.valueMeasures?.includes(fieldName)) {
        updateVisual(activeVisual, { valueMeasures: [...(currentViz.valueMeasures || []), fieldName] }, `Added "${fieldName}" to Values`);
        toast.success(`Added "${fieldName}" to Values axis`);
        emitCoachEvent('measure_added', 'report-measure-shelf');
      } else {
        toast.info(`"${fieldName}" is already in the visual`);
      }
    }
  };

  // ─── Model Selection Screen ───
  if (!modelChosen) {
    return (
      <ModelPicker models={models} onSelectModel={(id) => { setModelId(id); setModelChosen(true); emitCoachEvent('model_selected', 'model-picker'); }} onBack={() => confirmNavigation(() => navigate('/dashboards'))} />
    );
  }

  // ─── Shelf builder helpers ───
  // Merge date part labels into field overrides for pill display
  const effectiveOverrides = { ...fieldOverrides };
  Object.entries(dateParts).forEach(([field, part]) => {
    if (part) {
      const sourceField = allFields.find(f => f.name === field)?._sourceField || field;
      const baseMeta = allFields.find(f => f.name === sourceField) || allFields.find(f => f.name === field);
      const base = baseMeta?.description || sourceField.replace(/_/g, ' ');
      effectiveOverrides[field] = { ...(effectiveOverrides[field] || {}), displayName: `${base} (${part.replace('_', ' ')})` };
    } else if (field.includes('__')) {
      // Virtual field without date part yet — show base name
      const sourceField = allFields.find(f => f.name === field)?._sourceField || field.split('__')[0];
      const baseMeta = allFields.find(f => f.name === sourceField);
      const base = baseMeta?.description || sourceField.replace(/_/g, ' ');
      effectiveOverrides[field] = { ...(effectiveOverrides[field] || {}), displayName: `${base} (select date part)` };
    }
  });
  const shelfProps = { allFields, fieldOverrides: effectiveOverrides, onFieldProperties: openFieldProperties, onPillRightClick: handlePillRightClick };

  const renderShelves = () => {
    const isImportedActive = currentVisual && currentVisual._imported && !reviewedVisualIds.has(currentVisual.id);
    const rw = (setter) => (...args) => { if (isImportedActive) markActiveReviewed(); return setter(...args); };
    return (
    <ShelfBuilder
      vizType={currentVisual ? currentVisual.vizType : 'table'}
      rowDims={rowDims} colDim={colDim} valueMeasures={valueMeasures} filterDims={filterDims}
      colorMeasure={colorMeasure} setColorMeasure={rw(setColorMeasure)}
      allFields={allFields} fieldOverrides={effectiveOverrides} dateParts={dateParts}
      setRowDims={rw(setRowDims)} setColDim={rw(setColDim)} setValueMeasures={rw(setValueMeasures)} setFilterDims={rw(setFilterDims)}
      setDateParts={setDateParts} setSlicerSelections={setSlicerSelections}
      removeFromAllShelves={removeFromAllShelves}
      pinFieldEntity={pinFieldEntity}
      onFieldProperties={openFieldProperties} onPillRightClick={handlePillRightClick}
      onRowDimAdded={maybeAutoMapSwitch}
      guessedFields={isImportedActive ? (currentVisual._guessedFields || []) : []}
    />
    );
  };

  return (
    <div className={'h-full ' + (layoutPreset === 'stacked' ? 'flex flex-col' : 'flex flex-row')} data-testid="report-builder-page">
      {showImportReviewNote && (
        <div
          data-testid="import-mapping-review-note"
          className="fixed top-16 left-1/2 -translate-x-1/2 z-[100] flex items-start gap-2 max-w-lg px-4 py-2.5 rounded-lg shadow-lg border border-amber-300 bg-amber-50 text-amber-900"
        >
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
          <div className="text-xs leading-relaxed">
            <span className="font-semibold">Best-guess field mapping.</span>{' '}
            This report was auto-created from your file. Fields marked with an{' '}
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 align-middle" />{' '}
            dot are auto-mapped guesses — review each visual's drop zones and adjust any mismatches before saving.
            {(() => {
              const visibleUnmapped = importUnmappedColumns.filter(c => !resolvedUnmappedCols.has(String(c).toLowerCase()));
              return visibleUnmapped.length > 0 ? (
              <span className="block mt-1" data-testid="import-unmapped-columns">
                <span className="font-semibold">Couldn't match these columns</span> to your model — click to add:{' '}
                {visibleUnmapped.map((c, i) => (
                  <React.Fragment key={c}>
                    {i > 0 && ', '}
                    <button
                      type="button"
                      data-testid={'unmapped-col-' + c}
                      onClick={() => addUnmappedColumn(c)}
                      className="underline decoration-dotted underline-offset-2 font-medium hover:text-amber-700 hover:decoration-solid"
                      title={'Add "' + c + '" to a shelf'}
                    >
                      {c}
                    </button>
                  </React.Fragment>
                ))}
              </span>
              ) : null;
            })()}
          </div>
          <button
            type="button"
            data-testid="dismiss-import-mapping-review-note"
            onClick={() => {
              setShowImportReviewNote(false);
              try {
                const dkey = importSessionId || sessionStorage.getItem('importedSessionId') || '';
                if (dkey) localStorage.setItem('inzara.import_review_dismissed.' + dkey, '1');
              } catch (e) {}
            }}
            className="ml-1 shrink-0 text-amber-500 hover:text-amber-700"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {/* Pill right-click context menu */}
      <PillContextMenu
        pillCtxMenu={pillCtxMenu} setPillCtxMenu={setPillCtxMenu}
        allFields={allFields} dateParts={dateParts} setDateParts={setDateParts}
        slicerDisplayModes={slicerDisplayModes} setSlicerDisplayModes={setSlicerDisplayModes}
        secondYAxisConfig={secondYAxisConfig} setSecondYAxisConfig={setSecondYAxisConfig}
        activeVisual={activeVisual} visuals={visuals}
        openFieldProperties={openFieldProperties} handleBrowseField={handleBrowseField}
        removeFromAllShelves={removeFromAllShelves}
      />

      {/* ─── LEFT: Field Panel ─── */}
      <BinFieldDialog
        open={!!binField}
        onOpenChange={(v) => { if (!v) setBinField(null); }}
        field={binField}
        modelId={modelId}
        onCreated={reloadFieldsAfterBin}
      />
      {layoutPreset === 'default' && fieldsCollapsed ? (
        <div className="w-8 shrink-0 border-r border-border/50 flex flex-col items-center pt-2 gap-2" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)', order: 1 }}>
          <button onClick={() => setFieldsCollapsed(false)} data-testid="expand-fields-btn" title="Expand Fields panel" className="p-1 rounded hover:bg-primary/20 text-muted-foreground hover:text-primary transition-colors">
            <PanelRight className="w-4 h-4" />
          </button>
          <span className="text-[9px] text-muted-foreground tracking-wider" style={{ writingMode: 'vertical-rl' }}>Fields</span>
        </div>
      ) : (
        <LeftFieldPanel
          layoutPreset={layoutPreset}
          leftPanelTab={leftPanelTab} setLeftPanelTab={setLeftPanelTab}
          fieldSearch={fieldSearch} setFieldSearch={setFieldSearch}
          filteredDims={filteredDims} filteredMsrs={filteredMsrs} filteredCalcMsrs={filteredCalcMsrs}
          handleBrowseField={handleBrowseField} handleAddFieldToVisual={handleAddFieldToVisual} handleViewField={handleViewField} handleBinField={handleBinField}
          libraryVisuals={libraryVisuals} addLibraryVisual={addLibraryVisual}
          allFields={allFields} modelId={modelId} formatValue={formatValue}
          panelWidth={(layoutPreset === 'default' || layoutPreset === 'fields_right') ? fieldsPanelWidth : undefined}
        />
      )}
      {layoutPreset === 'default' && !fieldsCollapsed && (
        <div
          onMouseDown={(e) => startPanelResize(e, 'fields')}
          data-testid="resizer-fields-config"
          title="Drag to resize"
          className="group relative w-1.5 shrink-0 cursor-col-resize bg-transparent hover:bg-primary/40 active:bg-primary/60 transition-colors"
          style={{ order: 1 }}
        >
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setFieldsCollapsed(true)}
            data-testid="collapse-fields-btn"
            title="Collapse Fields panel"
            className="absolute top-14 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-muted/80 border border-border/50 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 opacity-0 group-hover:opacity-100 transition-opacity z-20 cursor-pointer"
          >
            <ChevronLeft className="w-3 h-3" />
          </button>
        </div>
      )}
      {layoutPreset === 'default' && configCollapsed && (
        <div className="w-8 shrink-0 border-r border-border/50 flex flex-col items-center pt-2 gap-2" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(8px)', order: 2 }}>
          <button onClick={() => setConfigCollapsed(false)} data-testid="expand-config-btn" title="Expand Config panel" className="p-1 rounded hover:bg-primary/20 text-muted-foreground hover:text-primary transition-colors">
            <PanelRight className="w-4 h-4" />
          </button>
          <span className="text-[9px] text-muted-foreground tracking-wider" style={{ writingMode: 'vertical-rl' }}>Config</span>
        </div>
      )}
      <div className={'border-r border-border/50 flex flex-col overflow-hidden ' + (layoutPreset === 'stacked' ? 'w-full max-h-[35%] border-b border-r-0' : layoutPreset === 'config_bottom' ? 'hidden' : (layoutPreset === 'default' ? (configCollapsed ? 'hidden' : 'shrink-0') : 'w-72'))} style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(8px)', order: layoutPreset === 'fields_right' ? 2 : 2, ...(layoutPreset === 'default' ? { width: configPanelWidth } : {}) }}>
        <BuilderHeader
          editReportId={editReportId} designerMode={designerMode} setDesignerMode={setDesignerMode}
          confirmNavigation={confirmNavigation} navigate={navigate}
          undo={undo} redo={redo} undoStack={undoStack} redoStack={redoStack} revertTo={revertTo}
          autoRefreshInterval={autoRefreshInterval} setAutoRefreshInterval={setAutoRefreshInterval}
          autoRunPaused={autoRunPaused} toggleAutoRunPaused={toggleAutoRunPaused}
          staleCount={staleVisualIds.size} runStaleVisuals={runStaleVisuals}
          bookmarkMenuOpen={bookmarkMenuOpen} setBookmarkMenuOpen={setBookmarkMenuOpen}
          bookmarks={bookmarks} setBookmarks={setBookmarks} reportId={reportId}
          crossFilter={crossFilter} setCrossFilter={setCrossFilter}
          slicerSelections={slicerSelections} setSlicerSelections={setSlicerSelections}
          globalFilterValues={globalFilterValues} setGlobalFilterValues={setGlobalFilterValues}
          paramValues={paramValues} setParamValues={setParamValues}
          reportName={reportName} setReportName={setReportName}
          setSavedClean={setSavedClean} onLabelChange={onLabelChange}
          reportDesc={reportDesc} setReportDesc={setReportDesc}
        />

        {/* Tab bar */}
        <div className="flex border-b border-border/50">
          {[['data', 'Data'], ['visuals', 'Visuals'], ['advanced', 'Advanced'], ...(designerMode !== 'report' ? [['pages', 'Pages']] : []), ...(designerMode === 'report' ? [['page_layout', 'Page Layout']] : [])].map(([key, label]) => (
            <button key={key} onClick={() => setMiddleTab(key)} className={`flex-1 py-2 text-[10px] font-semibold uppercase tracking-wider transition-colors ${middleTab === key ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`tab-${key}`}>{label}</button>
          ))}
        </div>

        <ScrollArea className="flex-1">
          {/* Data + Visuals tabs rendered by MiddleTabContent */}
          <MiddleTabContent
            middleTab={middleTab} designerMode={designerMode}
            currentVisual={currentVisual} activeVisual={activeVisual}
            renderShelves={renderShelves} visuals={visuals} reportName={reportName}
            pages={pages} setPages={setPages}
            activePageId={activePageId} setActivePageId={setActivePageId}
            setPageVisuals={setPageVisuals} reportPages={reportPages}
            pageVisuals={pageVisuals} gridLayouts={gridLayouts} setGridLayouts={setGridLayouts}
            pageNavStyle={pageNavStyle} setPageNavStyle={setPageNavStyle}
            markColor={markColor} setMarkColor={setMarkColor}
            markSize={markSize} setMarkSize={setMarkSize}
            markLabel={markLabel} setMarkLabel={setMarkLabel}
            markDetail={markDetail} setMarkDetail={setMarkDetail}
            addVisual={addVisual} setVisuals={setVisuals}
            setActiveVisual={setActiveVisual} setTextBlocks={setTextBlocks}
            setAnnotations={setAnnotations} getMaxZIndex={getMaxZIndex}
            updateVisual={updateVisual} removeVisual={removeVisual}
            recommendations={recommendations}
            visualResults={visualResults} rowDims={rowDims} valueMeasures={valueMeasures}
            loadingAiVisuals={loadingAiVisuals} setAiContents={setAiContents}
            getVizConfig={getVizConfig} setVizConfig={setVizConfig}
            showGrid={showGrid} setShowGrid={setShowGrid}
            showLegend={showLegend} setShowLegend={setShowLegend}
            refLineValue={refLineValue} setRefLineValue={setRefLineValue}
            refLineLabel={refLineLabel} setRefLineLabel={setRefLineLabel}
            parameters={parameters} allFields={allFields}
            themeActive={!!reportTheme}
            reportTableSpanPages={reportTableSpanPages} setReportTableSpanPages={setReportTableSpanPages}
            reportTableRepeatHeader={reportTableRepeatHeader} setReportTableRepeatHeader={setReportTableRepeatHeader}
            tableCalcs={tableCalcs} setTableCalcs={setTableCalcs}
            dualAxisField={dualAxisField} setDualAxisField={setDualAxisField}
            dataSets={dataSets} setDataSets={setDataSets}
          />

          {/* ═══ TAB: Advanced ═══ */}
          {middleTab === 'advanced' && (
            <AdvancedTab
              activeVisual={activeVisual} getVizConfig={getVizConfig} setVizConfig={setVizConfig}
              transformedData={transformedData} result={result}
              rowDims={rowDims} valueMeasures={valueMeasures} allFields={allFields} modelId={modelId}
              parameters={parameters} setParameters={setParameters}
              paramValues={paramValues} setParamValues={setParamValues}
              pageField={pageField} setPageField={setPageField}
              pageValues={pageValues} setPageValues={setPageValues}
              pageIndex={pageIndex} setPageIndex={setPageIndex}
              pageAnimating={pageAnimating} setPageAnimating={setPageAnimating}
              highlightField={highlightField} setHighlightField={setHighlightField}
              highlightValue={highlightValue} setHighlightValue={setHighlightValue}
              condRules={condRules} setCondRules={setCondRules}
            />
          )}
          {/* ═══ TAB: Page Layout (Report mode only) ═══ */}
          {middleTab === 'page_layout' && designerMode === 'report' && (
            <PageLayoutTab
              reportPageSize={reportPageSize} setReportPageSize={setReportPageSize}
              reportOrientation={reportOrientation} setReportOrientation={setReportOrientation}
              reportMargins={reportMargins} setReportMargins={setReportMargins}
              reportHeader={reportHeader} setReportHeader={setReportHeader}
              reportFooter={reportFooter} setReportFooter={setReportFooter}
              reportName={reportName} reportTableSpanPages={reportTableSpanPages}
              reportTableRepeatHeader={reportTableRepeatHeader}
              reportId={reportId} visuals={visuals} visualResults={visualResults}
              allFields={allFields} getTransformedData={getTransformedData} getVizConfig={getVizConfig}
              parameters={parameters} paramValues={paramValues} formatValue={formatValue}
              fieldOverrides={effectiveOverrides}
              API={API}
            />
          )}
        </ScrollArea>

        {reportId && designerMode === 'report' && (
          <div className="p-3 border-t border-border/50">
            <Button variant="outline" onClick={() => navigate(`/reports/view?id=${reportId}`)} disabled={saving} className="w-full rounded-lg text-[10px] h-7 active:scale-95 border-primary/40 text-primary hover:bg-primary/10" data-testid="rpt-preview-report-btn"><Eye className="w-3 h-3 mr-1" />Preview Report</Button>
          </div>
        )}
      </div>

      {layoutPreset === 'default' && !configCollapsed && (
        <div
          onMouseDown={(e) => startPanelResize(e, 'config')}
          data-testid="resizer-config-canvas"
          title="Drag to resize"
          className="group relative w-1.5 shrink-0 cursor-col-resize bg-transparent hover:bg-primary/40 active:bg-primary/60 transition-colors"
          style={{ order: 2 }}
        >
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setConfigCollapsed(true)}
            data-testid="collapse-config-btn"
            title="Collapse Config panel"
            className="absolute top-14 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-muted/80 border border-border/50 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 opacity-0 group-hover:opacity-100 transition-opacity z-20 cursor-pointer"
          >
            <ChevronLeft className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* ─── RIGHT: Canvas ─── */}
      <div ref={co.canvasRef} onMouseMove={co.onCanvasMouseMove} className="flex-1 flex flex-col overflow-hidden relative" style={{ order: layoutPreset === 'fields_right' ? 1 : 3 }}>
        <CoEditCursors cursors={co.cursors} />
        {coEnabled && coRoom && (
          <div
            ref={barElRef}
            className="fixed z-[60] rounded-lg px-2.5 py-1.5 border border-indigo-400/60 shadow-lg shadow-indigo-900/30 flex items-center gap-3"
            style={barPos
              ? { left: barPos.x, top: barPos.y, background: 'linear-gradient(90deg,#4f46e5,#0891b2)' }
              : { right: 16, bottom: 16, background: 'linear-gradient(90deg,#4f46e5,#0891b2)' }}
            data-testid="coedit-panel">
            <CoEditBar members={co.members} editor={co.editor} amEditor={amEditor} me={me} onRequest={co.requestControl} onTake={co.takeControl} onLeave={() => { setCoEnabled(false); toast.message('Left the co-editing session'); }} dragHandle={startBarDrag} onColor />
            <button onClick={() => setBuilderShareOpen(true)} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-white/15 text-white hover:bg-white/25" data-testid="builder-share-view-btn"><Users className="w-3 h-3" /> Share view</button>
          </div>
        )}
        {!coEnabled && coRoom && (
          <button onClick={() => setCoEnabled(true)} className="fixed z-[60] right-4 bottom-4 text-[11px] px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white shadow-lg hover:bg-indigo-500 flex items-center gap-1" data-testid="coedit-rejoin"><Users className="w-3 h-3" /> Rejoin co-editing</button>
        )}
        {!amEditor && (
          <div className="absolute inset-0 z-20 bg-background/40 cursor-not-allowed" title="View only — request edit control to make changes" data-testid="coedit-readonly-overlay" />
        )}
        <ShareViewDialog open={builderShareOpen} onOpenChange={setBuilderShareOpen} onShare={doShareBuilder} />
        {/* Global Filters - render based on position */}
        {(() => {
          const builderActions = (
            <TooltipProvider delayDuration={300}>
              <div className="flex items-center gap-1" data-testid="builder-action-toolbar">
                {editReportId && !savedClean && draftSavedAt > 0 && (
                  <span className="flex items-center gap-1 mr-1 text-[10px] text-emerald-600 dark:text-emerald-400 select-none" data-testid="draft-saved-indicator" title="Your in-progress edits are safely stored and will be restored if the page reloads">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Draft saved
                  </span>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="icon" variant="ghost" onClick={() => executeQuery()} disabled={loading || !modelId} className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 active:scale-95" data-testid="rpt-refresh-data-btn">
                      <RefreshCw className={'w-3.5 h-3.5 ' + (loading ? 'animate-spin' : '')} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Refresh data — rerun all queries</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" onClick={() => setSaveDialogOpen(true)} disabled={saving || !reportName.trim()} className="gap-1.5" data-testid="rpt-save-btn">
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      Save
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{reportId ? (designerMode === 'report' ? 'Save Report' : `Save ${getTerm('insights', 'singular')}`) : (designerMode === 'report' ? 'Create Report' : `Create ${getTerm('insights', 'singular')}`)}</TooltipContent>
                </Tooltip>
                {reportId && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="icon" variant="ghost" onClick={saveAs} disabled={saving} className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 active:scale-95" data-testid="rpt-saveas-btn">
                        <FilePlus2 className="w-3.5 h-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Save a copy under a new name</TooltipContent>
                  </Tooltip>
                )}
                {reportId && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="icon" variant="ghost" onClick={() => setVersionsOpen(true)} disabled={saving} className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 active:scale-95" data-testid="rpt-versions-btn">
                        <History className="w-3.5 h-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Version history{reportVersion != null ? ` · v${reportVersion}` : ''}</TooltipContent>
                  </Tooltip>
                )}
                {reportId && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="icon" variant="ghost" onClick={cloneReport} disabled={saving} className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 active:scale-95" data-testid="rpt-clone-btn">
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Clone</TooltipContent>
                  </Tooltip>
                )}
                {reportId && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="icon" variant="ghost" onClick={() => setPromoteOpen(true)} disabled={saving} className="h-7 w-7 rounded-lg text-emerald-400 hover:bg-emerald-500/10 active:scale-95" data-testid="rpt-promote-btn">
                        <Rocket className="w-3.5 h-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Promote (Dev → Test → Prod)</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="icon" variant="ghost" onClick={() => { const doClose = () => { if (isTabbed && onClose) onClose(); else navigate('/dashboards'); }; confirmNavigation(doClose); }} className="h-7 w-7 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 active:scale-95" data-testid="rpt-close-btn">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Close</TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          );
          const filterBar = (
            <GlobalFilterBar
              position={globalFilterPosition}
              onPositionChange={setGlobalFilterPosition}
              globalFilterDims={globalFilterDims}
              setGlobalFilterDims={setGlobalFilterDims}
              globalFilterValues={globalFilterValues}
              setGlobalFilterValues={setGlobalFilterValues}
              globalSlicerValues={globalSlicerValues}
              slicerDisplayModes={slicerDisplayModes}
              setSlicerDisplayModes={setSlicerDisplayModes}
              slicerSettings={slicerSettings}
              onOpenSettings={setSettingsDialogDim}
              onFieldProperties={openFieldProperties}
              fieldOverrides={effectiveOverrides}
              crossFilter={crossFilter}
              onClearCrossFilter={() => setCrossFilter(null)}
              dateFields={dateFields}
              defaultDateField={modelDefaultDateField && dateFields.includes(modelDefaultDateField) ? modelDefaultDateField : (dateFields[0] || undefined)}
              fiscalStartMonth={fiscalStartMonth}
              allFields={allFields}
              cascadeSlicers={cascadeSlicers}
              onToggleCascade={setCascadeSlicers}
              actions={builderActions}
            />
          );

          const setPageDims = (updater) => setPageFilterDims(prev => {
            const cur = prev[activePageId] || [];
            const next = typeof updater === 'function' ? updater(cur) : updater;
            return { ...prev, [activePageId]: next };
          });
          const setPageVals = (updater) => setPageFilterValues(prev => {
            const cur = prev[activePageId] || {};
            const next = typeof updater === 'function' ? updater(cur) : updater;
            return { ...prev, [activePageId]: next };
          });
          const pageFilterBar = (pages.length > 1 && activePageId) ? (
            <div className="px-3 pt-2" data-testid="page-scope-filter-bar">
              <GlobalFilterBar
                position="top"
                onPositionChange={() => {}}
                globalFilterDims={pageFilterDims[activePageId] || []}
                setGlobalFilterDims={setPageDims}
                globalFilterValues={pageFilterValues[activePageId] || {}}
                setGlobalFilterValues={setPageVals}
                globalSlicerValues={globalSlicerValues}
                slicerDisplayModes={slicerDisplayModes}
                setSlicerDisplayModes={setSlicerDisplayModes}
                slicerSettings={slicerSettings}
                onOpenSettings={setSettingsDialogDim}
                onFieldProperties={openFieldProperties}
                fieldOverrides={effectiveOverrides}
                crossFilter={null}
                onClearCrossFilter={() => {}}
                dateFields={dateFields}
                defaultDateField={modelDefaultDateField && dateFields.includes(modelDefaultDateField) ? modelDefaultDateField : (dateFields[0] || undefined)}
                fiscalStartMonth={fiscalStartMonth}
                allFields={allFields}
                cascadeSlicers={cascadeSlicers}
                onToggleCascade={setCascadeSlicers}
                actions={null}
              />
            </div>
          ) : null;

          return (
            <div className={`flex-1 flex ${globalFilterPosition === 'left' || globalFilterPosition === 'right' ? 'flex-row' : 'flex-col'} overflow-hidden`}>
              {globalFilterPosition === 'top' && filterBar}
              {globalFilterPosition === 'left' && filterBar}
              <div className="flex-1 flex flex-col overflow-hidden">
                {drillPath.length > 0 && (
          <div className="px-4 py-1.5 border-b border-border/30 flex items-center gap-1 text-[10px]" style={{ background: 'var(--glass-bg)' }}>
            <button onClick={() => { setDrillPath([]); setDrillFilters([]); setRowDims([drillPath[0].dim]); }} className="text-primary hover:underline">Root</button>
            {drillPath.map((d, i) => <span key={i} className="text-muted-foreground"> / {d.dim}={d.value}</span>)}
            <button onClick={drillUp} className="ml-2 text-primary hover:underline">Back</button>
          </div>
        )}
        {designerMode === 'report' && (
          /* ═══ REPORT MODE: Multi-page fixed canvas ═══ */
          <ReportCanvas 
            pageSize={reportPageSize} 
            orientation={reportOrientation} 
            margins={reportMargins} 
            zoom={reportZoom} 
            onZoomChange={setReportZoom} 
            showGrid={reportShowGrid} 
            onToggleGrid={() => setReportShowGrid(!reportShowGrid)}
            onLibraryDrop={(visId) => { const lv = libraryVisuals.find(v => v.visual_id === visId); if (lv) addLibraryVisual(lv); }} 
            snapSize={reportSnapSize} 
            onSnapChange={setReportSnapSize} 
            onCanvasClick={() => { setActiveVisual(''); setVisualCtxMenu(null); setAlignmentGuides({ x: null, y: null }); }}
            // Multi-page props
            pages={reportPages}
            activePageIndex={activeReportPageIndex}
            onPageChange={setActiveReportPageIndex}
            onAddPage={addReportPage}
            onDeletePage={deleteReportPage}
            onInsertPageBreak={insertManualPageBreak}
            // Header/Footer config
            headerConfig={reportHeader}
            footerConfig={reportFooter}
            // Alignment guides and rulers
            alignmentGuides={alignmentGuides}
            showRulers={reportShowRulers}
            onToggleRulers={() => setReportShowRulers(!reportShowRulers)}
            // Render content for each page
            renderPageContent={makeRenderPageContent({
              visuals, getTransformedData, visualResults,
              activeVisual, setActiveVisual, setActiveReportPageIndex,
              findAlignmentGuides, setAlignmentGuides,
              updateVisual, setSavedClean,
              handleVisualContextMenu, snap,
              getVizConfig, setVizConfig,
              formatValue, allFields, fieldOverrides,
              otherExpanded, toggleOtherExpanded,
              onViewQuery: (vizId) => { if (!visualQueryInfo[vizId]) executeVisualQuery(vizId); setQueryViewVisualId(vizId); setQueryViewOpen(true); },
              globalSpan: reportTableSpanPages,
              asOfDate: globalFilterValues['__asof__'],
              modelId,
            })}
            // Render header for each page
            renderHeader={makeRenderPageHeader({
              visuals, reportHeader, reportName,
              activeVisual, setActiveVisual,
              updateVisual, setSavedClean,
              handleVisualContextMenu, snap,
            })}
            // Render footer for each page
            renderFooter={makeRenderPageFooter({
              visuals, reportFooter, reportPages,
              activeVisual, setActiveVisual,
              updateVisual, setSavedClean,
              handleVisualContextMenu, snap,
            })}
          />
        )}
        <div ref={canvasRef} className="flex-1 overflow-auto p-6" data-testid="visual-canvas" data-coach-target="report-visual-canvas" style={{ display: designerMode === 'report' ? 'none' : undefined }}
          onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
          onDrop={e => { e.preventDefault(); const data = e.dataTransfer.getData('text/plain'); if (data && data.startsWith('library_visual:')) { const visId = data.replace('library_visual:', ''); const lv = libraryVisuals.find(v => v.visual_id === visId); if (lv) addLibraryVisual(lv); } }}>
          <div style={{ position: 'relative', minHeight: freeCanvasMinHeight || undefined }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-heading font-bold text-foreground">{reportName || 'Untitled Insight'}{reportVersion != null ? <span className="ml-2 text-base font-mono font-normal text-muted-foreground align-middle" data-testid="report-version-label">v{reportVersion}</span> : null}</h1>
                {reportDesc && <p className="text-xs text-muted-foreground mt-0.5">{reportDesc}</p>}
              </div>
              {loading && <Loader2 className="w-4 h-4 text-primary animate-spin" />}
              <div className="flex items-center gap-2">
                {result && <span className="text-[10px] text-muted-foreground">{result.row_count} rows</span>}
                {result && <Button variant="outline" size="sm" onClick={() => setStatsOpen(true)} className="rounded-lg text-[9px] h-6 px-2" data-testid="report-stats-btn"><Info className="w-3 h-3 mr-1" />Stats</Button>}
                {result && <Button variant="outline" size="sm" onClick={() => setExportOpen(true)} className="rounded-lg text-[9px] h-6 px-2" data-testid="export-btn"><ArrowLeft className="w-3 h-3 mr-1 rotate-[135deg]" />Export</Button>}
                {highlightField && <Badge className="text-[8px] bg-amber-500/15 text-amber-400 border-amber-500/20 gap-1"><span>Highlight: {highlightValue || 'all'}</span><button onClick={() => { setHighlightField(null); setHighlightValue(null); }}><X className="w-2 h-2" /></button></Badge>}
                {pageField && pageValues.length > 0 && <Badge className="text-[8px] bg-indigo-500/15 text-indigo-400 border-indigo-500/20 gap-1"><span>Page: {pageField.replace(/_/g, ' ')} = {pageValues[pageIndex]}</span></Badge>}
                <div className="relative ml-1">
                  <Button variant="outline" size="sm" onClick={() => setThemePickerOpen(!themePickerOpen)} className="rounded-lg text-[9px] h-6 px-2" data-testid="theme-picker-btn">
                    {reportTheme ? <span className="w-2 h-2 rounded-sm mr-1" style={{ background: reportTheme.colors?.[0] || '#3b82f6', display: 'inline-block' }} /> : <Palette className="w-3 h-3 mr-1" />}
                    {reportTheme?.name || 'Theme'}
                  </Button>
                  {themePickerOpen && (
                    <React.Fragment>
                      <div className="fixed inset-0 z-40" onClick={() => setThemePickerOpen(false)} />
                      <div className="absolute top-full right-0 mt-1 glass-card rounded-lg shadow-2xl border border-border/50 py-1 w-56 z-50 max-h-64 overflow-auto">
                        <button onClick={() => { setReportTheme(null); setThemePickerOpen(false); }} className={'w-full text-left px-3 py-1.5 text-[10px] transition-colors ' + (!reportTheme ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted/30')}>Default (from config)</button>
                        {availableThemes.map(t => (
                          <button key={t.theme_id} onClick={() => { setReportTheme({ theme_id: t.theme_id, name: t.name, colors: t.colors }); setThemePickerOpen(false); }} className={'w-full flex items-center gap-2 px-3 py-1.5 text-[10px] transition-colors ' + (reportTheme?.theme_id === t.theme_id ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted/30')}>
                            <div className="flex gap-0.5 flex-shrink-0">{t.colors.slice(0, 5).map((c, i) => <span key={`${c}-${i}`} className="w-2 h-2 rounded-sm" style={{ background: c }} />)}</div>
                            {t.name}
                          </button>
                        ))}
                      </div>
                    </React.Fragment>
                  )}
                </div>
                <div className="relative ml-1">
                  <Button variant="outline" size="sm" onClick={() => setLayoutMenuOpen(!layoutMenuOpen)} className="rounded-lg text-[9px] h-6 px-2" data-testid="layout-picker-btn"><LayoutGridIcon className="w-3 h-3 mr-1" />Designer Layout</Button>
                  {layoutMenuOpen && (
                    <React.Fragment>
                      <div className="fixed inset-0 z-40" onClick={() => setLayoutMenuOpen(false)} />
                      <div className="absolute top-full right-0 mt-1 glass-card rounded-lg shadow-2xl border border-border/50 py-1 w-56 z-50">
                        {Object.entries(LAYOUTS).map(([key, layout]) => (
                          <button key={key} onClick={() => setLayout(key)} className={'w-full text-left px-3 py-1.5 text-[10px] transition-colors ' + (layoutPreset === key ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted/30 hover:text-foreground')}>
                            {layout.name}
                          </button>
                        ))}
                        <div className="border-t border-border/50 my-1" />
                        <button onClick={resetPanelSizes} data-testid="reset-panel-sizes-btn" className="w-full text-left px-3 py-1.5 text-[10px] text-muted-foreground hover:bg-muted/30 hover:text-foreground transition-colors flex items-center gap-1.5">
                          <RotateCcw className="w-3 h-3" /> Reset panel sizes
                        </button>
                      </div>
                    </React.Fragment>
                  )}
                </div>
              </div>
            </div>
            {/* Report Header Zone (appears once at top, report mode only) */}
            {designerMode === 'report' && (
              <ReportZoneArea
                kind="report_header"
                visuals={visuals} visualResults={visualResults}
                activeVisual={activeVisual} setActiveVisual={setActiveVisual}
                updateVisual={updateVisual} setSavedClean={setSavedClean}
                handleVisualContextMenu={handleVisualContextMenu}
                getTransformedData={getTransformedData}
                getMaxZIndex={getMaxZIndex} setVisuals={setVisuals}
                reportHeader={reportHeader} reportFooter={reportFooter}
                allFields={allFields} formatValue={formatValue}
              />
            )}
            {/* Page Header Zone (repeats on every page, report mode only) */}
            {designerMode === 'report' && (
              <PageZoneArea
                kind="header"
                reportHeader={reportHeader} reportFooter={reportFooter}
                visuals={visuals} visualResults={visualResults}
                activeVisual={activeVisual} setActiveVisual={setActiveVisual}
                updateVisual={updateVisual} setSavedClean={setSavedClean}
                handleVisualContextMenu={handleVisualContextMenu}
                getTransformedData={getTransformedData}
                getMaxZIndex={getMaxZIndex} setVisuals={setVisuals}
                allFields={allFields} formatValue={formatValue}
                parameters={parameters} paramValues={paramValues}
              />
            )}
            {/* Page Navigation (dashboard mode multi-page) */}
            <PageNavChrome style={pageNavStyle} pages={pages} activeId={activePageId} onChange={setActivePageId} showHidden>
            {pageFilterBar}
            {/* Build grid items (excluding free-positioned objects) + text blocks + annotations */}
            {(() => {
              const gridVisuals = visuals.filter(v => !isObjectVisual(v.vizType) && !v._zone);
              const freeVis = visuals.filter(v => isObjectVisual(v.vizType) && !v._zone);
              const allItems = [
                ...gridVisuals.map(v => ({ type: 'visual', id: v.id, data: v })),
                ...textBlocks.map(tb => ({ type: 'text', id: tb.id, data: tb })),
                ...annotations.map((ann, i) => ({ type: 'annotation', id: ann.id || `ann_${i}`, data: ann, index: i })),
              ];
              const defaultLayout = allItems.map((item, i) => ({
                i: item.id,
                x: (i % 2) * 6,
                y: Math.floor(i / 2) * 4,
                w: allItems.length === 1 ? 12 : 6,
                h: item.type === 'visual' ? 4 : 2,
                minW: 2, minH: 1,
              }));
              const savedLayout = gridLayouts?.lg || defaultLayout;
              // Merge: use saved positions for known items, add defaults for new ones
              const layout = allItems.map((item, i) => {
                const saved = savedLayout.find(l => l.i === item.id);
                return saved || defaultLayout[i];
              });
              return (
                <>
                <ResponsiveGridLayout
                  className="layout"
                  layouts={{ lg: layout, md: layout, sm: layout, xs: layout, xxs: layout }}
                  breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                  cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
                  rowHeight={80}
                  width={Math.max((canvasWidth > 100 ? canvasWidth : 800) - ((['left_sidebar', 'right_sidebar'].includes(normalizeNavStyle(pageNavStyle)) && pages.filter(p => p.visible !== false).length > 1) ? 176 : 0), 320)}
                  onLayoutChange={(current) => setGridLayouts(prev => {
                    const min = (current || []).map(({ i, x, y, w, h }) => ({ i, x, y, w, h }));
                    if (_gridLayoutsEqual(prev?.lg, min)) return prev;
                    return { ...(prev || {}), lg: min };
                  })}
                  isDraggable
                  isResizable
                  draggableHandle=".drag-handle"
                  margin={[16, 16]}
                  autoSize={designerMode === 'report'}
                >
                  {allItems.map(item => {
                    if (item.type === 'visual') {
                      const v = item.data;
                      const isCrossFiltered = crossFilter && crossFilter.sourceVisualId !== v.id;
                      const isVisualLoading = loadingVisuals.has(v.id);
                      return (
                        <div key={v.id} data-visual-id={v.id} className={`glass-card rounded-2xl ${v.vizType === 'kpi' ? 'p-2' : 'p-5'} transition-all relative ${activeVisual === v.id ? 'ring-4 ring-primary ring-offset-2 ring-offset-background shadow-lg shadow-primary/20' : (isCrossFiltered ? '' : 'hover:ring-2 hover:ring-primary/25')} ${isCrossFiltered ? 'ring-1 ring-indigo-500/20' : ''} ${designerMode === 'report' && v.vizType === 'table' && reportTableSpanPages ? 'overflow-visible' : 'overflow-hidden'}`} style={{ zIndex: v.zIndex || 'auto' }} onClick={() => setActiveVisual(v.id)} onContextMenu={(e) => handleVisualContextMenu(e, v.id)}>
                          {isVisualLoading && (
                            <div className="absolute inset-0 z-20 bg-background/50 flex items-center justify-center rounded-2xl backdrop-blur-[2px]">
                              <Loader2 className="w-6 h-6 text-primary animate-spin" />
                            </div>
                          )}
                          {!isVisualLoading && (visualResults[v.id] || {}).error && (
                            <div className="absolute inset-0 z-10 bg-background/80 flex items-center justify-center rounded-2xl p-4" data-testid={`visual-error-${v.id}`}>
                              <div className="flex items-start gap-2 max-w-md text-left">
                                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                                <p className="text-[11px] leading-snug text-muted-foreground">{(visualResults[v.id] || {}).error}</p>
                              </div>
                            </div>
                          )}
                          <div className="flex items-center justify-between mb-1 drag-handle cursor-move relative z-30">
                            <p className="text-xs font-heading font-semibold text-foreground">{v.title}</p>
                            <div className="flex items-center gap-1" onMouseDown={e => e.stopPropagation()}>
                              {(visualResults[v.id] || {}).result && <TileMeasureControls result={(visualResults[v.id] || {}).result} canCertify={canCertify} onCertified={() => executeVisualQuery(v.id)} />}
                              {isCrossFiltered && <Badge className="text-[7px] bg-indigo-500/20 text-indigo-400 border-indigo-500/30">filtered</Badge>}
                              {staleVisualIds.has(v.id) && <Badge className="text-[7px] bg-amber-500/20 text-amber-400 border-amber-500/30" data-testid={`stale-badge-${v.id}`}>Changes not run yet</Badge>}
                              {staleVisualIds.has(v.id) && <button onClick={e => { e.stopPropagation(); executeVisualQuery(v.id); }} className="text-amber-400/70 hover:text-amber-400 p-0.5 rounded hover:bg-amber-500/10" title="Run this visual now" data-testid={`run-visual-${v.id}`}><Zap className="w-3 h-3" /></button>}
                              {getVizConfig(v.id)?.pinnedCorrelation && <button onClick={e => { e.stopPropagation(); setCorrPopupData({ vizId: v.id, data: getVizConfig(v.id).pinnedCorrelation }); }} className="text-emerald-400/60 hover:text-emerald-400 p-0.5 rounded hover:bg-emerald-500/10" title="What Drives This?" data-testid={`pinned-corr-${v.id}`}><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg></button>}
                              {v.vizType !== 'ai_text' && <button onClick={e => { e.stopPropagation(); if (!visualQueryInfo[v.id]) executeVisualQuery(v.id); setQueryViewVisualId(v.id); setQueryViewOpen(true); }} className="inline-flex items-center justify-center text-primary/80 hover:text-primary p-1 rounded-md border border-primary/25 hover:bg-primary/10 transition-colors" title="View executed query" aria-label="View executed query" data-testid={`view-query-${v.id}`}><Code className="w-3.5 h-3.5" /></button>}
                            </div>
                          </div>
                          {v.description && <p className="text-[10px] text-muted-foreground mb-2">{v.description}</p>}
                          {/* Per-visual filters inside card */}
                          {(v.filterDims || []).length > 0 && (
                            <div className="flex gap-1.5 mb-2 flex-wrap">
                              {(v.filterDims || []).map(dim => {
                                // Global Slicer Sync: if this slicer's dimension is
                                // also a page-level filter, the in-tile slicer and the
                                // page filter share the SAME value list and stay in
                                // lockstep — selecting here drives the global filter
                                // (which reruns every visual), so both reflect it.
                                const isGlobalDim = globalFilterDims.includes(dim);
                                const globalVal = globalFilterValues[dim];
                                const localKey = v.id + '_' + dim;
                                const localVal = slicerSelections[localKey];
                                const effectiveVal = globalVal || localVal;
                                const sharedValues = (isGlobalDim ? (globalSlicerValues[dim] || slicerValues[dim]) : slicerValues[dim]) || [];
                                const handleSlicerChange = val => {
                                  if (isGlobalDim) {
                                    setGlobalFilterValues(p => { const n = { ...p }; if (val) n[dim] = val; else delete n[dim]; return n; });
                                  } else {
                                    setSlicerSelections(p => ({ ...p, [localKey]: val }));
                                  }
                                };
                                return <SlicerControl key={dim} dimension={dim} label={resolveLabel(allFields, dim)} values={sortSlicerValues(sharedValues, getSlicerSetting(slicerSettings, dim).sortOrder)} selected={effectiveVal} onChange={handleSlicerChange} displayMode={slicerDisplayModes[dim]} onDisplayModeChange={(d, m) => setSlicerDisplayModes(prev => ({ ...prev, [d]: m }))} isDate={isDateField(allFields.find(f => f.name === dim))} fiscalStartMonth={fiscalStartMonth} settings={getSlicerSetting(slicerSettings, dim)} onOpenSettings={setSettingsDialogDim} />;
                              })}
                            </div>
                          )}
                          <div className={`h-[calc(100%-32px)] ${['table','pivot','loss_triangle','ai_text','rich_text'].includes(v.vizType) ? 'overflow-auto' : 'overflow-hidden'}`}>
                            <VisualErrorBoundary resetKey={`${v.vizType}-${(v.rowDims||[]).length}-${(v.valueMeasures||[]).length}`} label={v.title || v.vizType}>
                            {v.vizType === 'kpi' ? (() => {
                              const vr = (visualResults[v.id] || {}).result;
                              const vm = v.valueMeasures || [];
                              const val = vr?.data?.[0]?.[vm[0]] ?? null;
                              const fmtVal = val !== null ? formatValue(val, vm[0]) : '--';
                              const label = allFields.find(f => f.name === vm[0])?.description || vm[0]?.replace(/_/g, ' ') || 'Value';
                              const indicatorColor = v.kpiIndicatorColor === 'red' ? 'text-red-400' : v.kpiIndicatorColor === 'neutral' ? 'text-muted-foreground' : 'text-emerald-400';
                              return (
                                <div className="flex items-center justify-between h-full px-3">
                                  <div className="flex flex-col justify-center min-w-0">
                                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider leading-tight">{label}</p>
                                    <p className="text-2xl font-heading font-bold text-foreground leading-tight">{fmtVal}</p>
                                    {v.kpiIndicator && <p className={`text-[10px] mt-0.5 ${indicatorColor}`}>{v.kpiIndicator}</p>}
                                  </div>
                                  {getKpiIcon(v.kpiIcon) && (
                                    <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary flex-shrink-0">
                                      {getKpiIcon(v.kpiIcon)}
                                    </div>
                                  )}
                                </div>
                              );
                            })() : v.vizType === 'ai_text' ? (
                              <div className="p-3 relative">
                                {loadingAiVisuals.has(v.id) && (
                                  <div className="absolute inset-0 z-20 bg-background/50 flex items-center justify-center rounded-2xl backdrop-blur-[2px]">
                                    <div className="flex items-center gap-2"><Loader2 className="w-5 h-5 text-indigo-400 animate-spin" /><span className="text-xs text-indigo-400 font-medium">AI generating...</span></div>
                                  </div>
                                )}
                                {(aiContents[v.id] || v.aiContent) ? <div className="text-xs text-foreground/80 whitespace-pre-wrap leading-relaxed">{aiContents[v.id] || v.aiContent}</div> : <p className="text-xs text-muted-foreground/70 italic">Configure AI prompt in Visuals tab and click Generate</p>}
                              </div>
                            ) : v.vizType === 'image' ? (
                              <div className="h-full flex items-center justify-center p-2">
                                {v.imageUrl ? <img src={v.imageUrl} alt={v.title} className={'max-w-full max-h-full ' + (v.imageBorder ? 'border-2 border-border/50 rounded-lg ' : '') + 'object-' + (v.imageFit || 'contain')} /> : <p className="text-xs text-muted-foreground/70 italic">Set image URL or upload in Visuals tab</p>}
                              </div>
                            ) : v.vizType === 'rich_text' ? (
                              <div className={`h-full p-1 overflow-auto flex ${v.textVAlign === 'middle' ? 'items-center' : v.textVAlign === 'bottom' ? 'items-end' : 'items-start'}`} onClick={e => e.stopPropagation()}>
                                <textarea value={v.textContent || ''} onChange={e => updateVisual(v.id, { textContent: e.target.value })} className="w-full flex-1 bg-transparent border-none outline-none resize-none p-0 m-0" style={{ fontSize: v.textSize || 16, color: v.textColor || 'inherit', textAlign: v.textAlign || 'left', fontWeight: v.textBold ? 'bold' : 'normal', fontStyle: v.textItalic ? 'italic' : 'normal', lineHeight: 1.2 }} placeholder="Type here..." />
                              </div>
                            ) : v.vizType === 'shape' ? (
                              <div className="h-full flex items-center justify-center p-2">
                                {(v.shapeType === 'rectangle' || !v.shapeType) && <div className="w-full h-full" style={{ background: v.shapeFill || '#3b82f6', opacity: v.shapeOpacity || 0.3, border: `2px solid ${v.shapeBorder || '#1e40af'}`, borderRadius: v.shapeBorderRadius || 12 }} />}
                                {v.shapeType === 'circle' && <div className="w-full h-full" style={{ background: v.shapeFill || '#3b82f6', opacity: v.shapeOpacity || 0.3, border: `2px solid ${v.shapeBorder || '#1e40af'}`, borderRadius: '50%' }} />}
                                {v.shapeType === 'line_h' && <div className="w-full" style={{ height: 3, background: v.shapeFill || '#3b82f6', opacity: v.shapeOpacity || 1 }} />}
                                {v.shapeType === 'line_v' && <div className="h-full" style={{ width: 3, background: v.shapeFill || '#3b82f6', opacity: v.shapeOpacity || 1 }} />}
                              </div>
                            ) : v.vizType === 'pivot' ? (
                              <PivotTableRenderer pivotResult={visualResults[v.id]?.pivotResult} vizConfig={getVizConfig(v.id)} formatValue={formatValue} allFields={allFields} onDrill={(val) => handleCrossFilter(val, v.id)} />
                            ) : v.vizType === 'loss_triangle' ? (
                              <LossTriangleRenderer analysis={visualResults[v.id]?.triangleResult} vizConfig={getVizConfig(v.id)} formatValue={formatValue} />
                            ) : v.vizType === 'forecast' ? (
                              <ForecastTile snapshot={v.forecastSnapshot} meta={v.forecastMeta} formatValue={fmtForVisual(v.id)} sentinel={v.sentinel} />
                            ) : v.vizType === 'sentinel_summary' ? (
                              <SentinelSummaryTile />
                            ) :
                              (() => {
                                // Check if period comparison is active
                                const compResult = visualResults[v.id]?.comparisonResult;
                                if (compResult?.data?.length > 0) {
                                  return <ComparisonTableRenderer result={compResult} vizConfig={getVizConfig(v.id)} formatValue={formatValue} allFields={allFields} />;
                                }
                                const vData = getTransformedData(v.id); const vResult = (visualResults[v.id] || {}).result; const vDims = v.rowDims || []; const vCol = v.colDim || ''; const vFmt = fmtForVisual(v.id); return v.vizType === 'table' ? <TableRenderer result={vData ? { ...vResult, data: vData } : vResult} vizConfig={getVizConfig(v.id)} formatValue={vFmt} allFields={allFields} fieldOverrides={fieldOverrides} rowDims={vDims} onDrill={(val) => handleCrossFilter(val, v.id)} printMode={designerMode === 'report' && reportTableSpanPages} onVizConfigChange={(cfg) => setVizConfig(v.id, cfg)} /> :
                              <div className="relative h-full">{getVizConfig(v.id)?.seriesTopN?.enabled && otherExpanded[v.id] && (<button onClick={() => toggleOtherExpanded(v.id)} data-testid={`collapse-other-${v.id}`} className="absolute top-1 right-1 z-10 text-[9px] px-2 py-0.5 rounded-full bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors">← Collapse “Other”</button>)}<ChartRenderer vizType={v.vizType} data={vData} rowDims={vDims} colDim={vCol} showGrid={showGrid} showLegend={showLegend} refLineValue={resolvedRefLineValue} refLineLabel={refLineLabel} vizConfig={getVizConfig(v.id)} formatValue={vFmt} allFields={allFields} fieldOverrides={fieldOverrides} onDrill={(val) => handleCrossFilter(val, v.id)} highlightField={highlightField} highlightValue={highlightValue} secondYAxis={secondYAxisConfig[v.id]} themeColors={reportTheme?.colors} valueMeasures={v.valueMeasures} marks={{ color: markColor, size: markSize, label: markLabel, detail: markDetail }} onOtherToggle={getVizConfig(v.id)?.seriesTopN?.enabled ? () => toggleOtherExpanded(v.id) : undefined} onToggleStack={(next) => setVizConfig(v.id, { barLayout: next })} /></div>; })()}
                            {!['ai_text','kpi','image','rich_text','shape','forecast','sentinel_summary','loss_triangle','pivot'].includes(v.vizType) && !(visualResults[v.id]?.result?.data?.length) && !loading && !loadingVisuals.has(v.id) && (
                              <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                                <div className="w-12 h-12 rounded-full bg-muted/30 flex items-center justify-center mb-3">
                                  <BarChart3 className="w-6 h-6 text-muted-foreground/70" />
                                </div>
                                <p className="text-sm text-muted-foreground font-medium mb-1">No data configured</p>
                                <p className="text-xs text-muted-foreground/80 max-w-[200px]">
                                  Double-click or drag fields from the left panel to add data to this visual
                                </p>
                              </div>
                            )}
                            </VisualErrorBoundary>
                          </div>
                        </div>
                      );
                    }
                    if (item.type === 'text') {
                      const tb = item.data;
                      return (
                        <div key={tb.id} className="glass-card rounded-2xl p-4 group/tb overflow-hidden">
                          <div className="flex items-center justify-between mb-2 drag-handle cursor-move">
                            <span className="text-[9px] text-muted-foreground">Text Block</span>
                            <button onClick={() => setTextBlocks(prev => prev.filter(t => t.id !== tb.id))} className="opacity-0 group-hover/tb:opacity-100 text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
                          </div>
                          <textarea value={tb.text} onChange={e => setTextBlocks(prev => prev.map(t => t.id === tb.id ? { ...t, text: e.target.value } : t))} className="w-full h-[calc(100%-32px)] bg-transparent border-none outline-none text-foreground resize-none" style={{ fontSize: tb.fontSize || 14 }} />
                        </div>
                      );
                    }
                    if (item.type === 'annotation') {
                      const ann = item.data;
                      const ai = item.index;
                      return (
                        <div key={item.id} className="glass-card rounded-xl p-3 border-l-4 border-amber-500/50 group/ann overflow-hidden">
                          <div className="flex items-center justify-between mb-1 drag-handle cursor-move">
                            <span className="text-[9px] text-amber-400/70">Annotation</span>
                            <button onClick={() => setAnnotations(prev => prev.filter((_, i) => i !== ai))} className="opacity-0 group-hover/ann:opacity-100 text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
                          </div>
                          <textarea value={ann.text} onChange={e => setAnnotations(prev => prev.map((a, i) => i === ai ? { ...a, text: e.target.value } : a))} className="w-full h-[calc(100%-24px)] bg-transparent border-none outline-none text-foreground text-xs resize-none" placeholder="Add annotation..." />
                        </div>
                      );
                    }
                    return null;
                  })}
                </ResponsiveGridLayout>
                {/* Free-positioned object visuals (Image, Text, Shape) */}
                {freeVis.map(v => (
                  <FreeObjectVisual
                    key={v.id} v={v}
                    isActive={activeVisual === v.id}
                    onSelect={() => setActiveVisual(v.id)}
                    onUpdate={(patch) => updateVisual(v.id, patch)}
                    onContextMenu={(e) => handleVisualContextMenu(e, v.id)}
                    setSavedClean={setSavedClean}
                  />
                ))}
              </>
              );
            })()}
            </PageNavChrome>
          </div>

          {/* Page Footer Zone (repeats on every page, report mode only) */}
          {designerMode === 'report' && (
            <PageZoneArea
              kind="footer"
              reportHeader={reportHeader} reportFooter={reportFooter}
              visuals={visuals} visualResults={visualResults}
              activeVisual={activeVisual} setActiveVisual={setActiveVisual}
              updateVisual={updateVisual} setSavedClean={setSavedClean}
              handleVisualContextMenu={handleVisualContextMenu}
              getTransformedData={getTransformedData}
              getMaxZIndex={getMaxZIndex} setVisuals={setVisuals}
              allFields={allFields} formatValue={formatValue}
              parameters={parameters} paramValues={paramValues}
            />
          )}

          {/* Report Footer Zone (appears once at end, report mode only) */}
          {designerMode === 'report' && (
            <ReportZoneArea
              kind="report_footer"
              visuals={visuals} visualResults={visualResults}
              activeVisual={activeVisual} setActiveVisual={setActiveVisual}
              updateVisual={updateVisual} setSavedClean={setSavedClean}
              handleVisualContextMenu={handleVisualContextMenu}
              getTransformedData={getTransformedData}
              getMaxZIndex={getMaxZIndex} setVisuals={setVisuals}
              reportHeader={reportHeader} reportFooter={reportFooter}
              allFields={allFields} formatValue={formatValue}
            />
          )}

        </div>
              {globalFilterPosition === 'bottom' && filterBar}
              </div>
              {globalFilterPosition === 'right' && filterBar}
            </div>
          );
        })()}
      </div>

      {/* All dialogs / context menus */}
      <Dialog open={saveAsOpen} onOpenChange={setSaveAsOpen}>
        <DialogContent className="max-w-sm" data-testid="save-as-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">Save As a new insight</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider">New name</label>
            <Input
              autoFocus
              value={saveAsName}
              onChange={(e) => setSaveAsName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && saveAsName.trim() && !saving) doSaveAs(); }}
              placeholder="Enter a name"
              data-testid="save-as-name-input"
              className="rounded-lg bg-muted/50 border-border/50"
            />
            <p className="text-[10px] text-muted-foreground/80">Saves a brand-new copy; the original stays unchanged.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveAsOpen(false)} className="rounded-lg text-xs h-8">Cancel</Button>
            <Button onClick={doSaveAs} disabled={saving || !saveAsName.trim()} data-testid="save-as-confirm-btn" className="rounded-lg text-xs h-8 bg-primary text-primary-foreground">
              {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Saving…</> : <><Save className="w-3.5 h-3.5 mr-1" />Save As</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BuilderDialogs
        visualCtxMenu={visualCtxMenu} setVisualCtxMenu={setVisualCtxMenu}
        visuals={visuals} designerMode={designerMode} reportId={reportId}
        setLibraryVisuals={setLibraryVisuals} updateVisual={updateVisual}
        handleZIndexChange={handleZIndexChange}
        handleDeleteFromCtx={handleDeleteFromCtx}
        handleDuplicateFromCtx={handleDuplicateFromCtx}
        handleCreateAlertFromViz={handleCreateAlertFromViz}
        vizPlacementOpen={vizPlacementOpen} confirmVizPlacement={confirmVizPlacement}
        statsOpen={statsOpen} setStatsOpen={setStatsOpen}
        result={result} queryExplanation={queryExplanation}
        exportOpen={exportOpen} setExportOpen={setExportOpen}
        exportFormat={exportFormat} setExportFormat={setExportFormat}
        exportSettings={exportSettings} setExportSettings={setExportSettings}
        exporting={exporting} handleExport={handleExport}
        propsOpen={propsOpen} setPropsOpen={setPropsOpen}
        propsField={propsField} propsForm={propsForm} setPropsForm={setPropsForm}
        allFields={allFields} saveFieldProperties={saveFieldProperties}
        browseOpen={browseOpen} setBrowseOpen={setBrowseOpen}
        browseField={browseField} browseData={browseData} browseLoading={browseLoading}
        formatValue={formatValue}
        UnsavedDialog={UnsavedDialog}
        saveDialogOpen={saveDialogOpen} setSaveDialogOpen={setSaveDialogOpen}
        saveReport={saveReport} saving={saving} reportName={reportName} reportVersion={reportVersion}
        ConfirmDialogElement={ConfirmDialogElement}
        queryViewOpen={queryViewOpen} setQueryViewOpen={setQueryViewOpen}
        queryViewVisualId={queryViewVisualId} visualQueryInfo={visualQueryInfo}
        alertFromVizOpen={alertFromVizOpen} setAlertFromVizOpen={setAlertFromVizOpen}
        alertFromVizData={alertFromVizData} setAlertFromVizData={setAlertFromVizData}
        corrPopupData={corrPopupData} setCorrPopupData={setCorrPopupData}
        setVizConfig={setVizConfig}
      />
      <FieldStatsModal modelId={modelId} field={viewDataField} onClose={() => setViewDataField(null)} />
      {reportId && (
        <ReportVersionsDialog
          open={versionsOpen}
          onOpenChange={setVersionsOpen}
          reportId={reportId}
          currentVersion={reportVersion}
          onRolledBack={() => window.location.reload()}
        />
      )}
      {settingsDialogDim && (
        <SlicerSettingsDialog
          open={!!settingsDialogDim}
          onOpenChange={(o) => { if (!o) setSettingsDialogDim(null); }}
          dimension={settingsDialogDim}
          label={resolveLabel(allFields, settingsDialogDim)}
          isDate={isDateField(allFields.find(f => f.name === settingsDialogDim))}
          values={globalSlicerValues[settingsDialogDim] || slicerValues[settingsDialogDim] || []}
          measures={allFields.filter(f => f.semantic_type === 'measure').map(f => ({ name: f.name, label: f.display_name || f.description }))}
          visuals={(visuals || []).map(v => ({ id: v.id, title: v.title || v.name || v.id }))}
          pages={(pages || []).map(pg => ({ id: pg.id, name: pg.name || pg.id, visualIds: ((pageVisuals && pageVisuals[pg.id]) || (visuals || []).filter(v => (v.pageId || v._reportPageId) === pg.id)).map(v => v.id) }))}
          currentSettings={slicerSettings[settingsDialogDim]}
          onSave={handleSaveSlicerSetting}
        />
      )}
      {reportId && <PromotionDialog open={promoteOpen} onOpenChange={setPromoteOpen} reportId={reportId} />}
      {slowQuery && loadingVisuals.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 rounded-full bg-background/95 border border-border/60 shadow-lg backdrop-blur" data-testid="builder-slow-query-pill">
          <Loader2 className="w-4 h-4 text-primary animate-spin" />
          <span className="text-xs text-muted-foreground">This query is taking longer than usual…</span>
          <Button variant="outline" size="sm" onClick={cancelRunningQuery} className="h-7 rounded-full gap-1.5 text-xs" data-testid="cancel-query-btn">
            <X className="w-3.5 h-3.5" /> Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

